/**
 * Insights - Singleton for analytics tracking and third-party integrations
 *
 * SINGLETON: Central analytics service integrating multiple tracking providers
 * (Google Analytics, Facebook Pixel, Segment, etc.). Named "Insights" to avoid
 * ad blocker blacklists that target "analytics" keyword.
 *
 * PRIVACY: Respects user consent for functional, analytics, and marketing cookies.
 *
 * INTEGRATIONS:
 * - Google Analytics (GA4)
 * - Facebook Pixel
 * - Segment
 * - Pinterest, TikTok, Snapchat, Twitter
 * - Sift Science (fraud detection)
 * - Riskified, AdRoll, Partnerize
 *
 * @class Insights
 * @extends Class
 * @singleton
 */
define("Insights", [`Class`, `module`], function (Super, { id: namespace }) {
	// the key for the cookie that tracks the current FE session (sift science)
	const CLIENT_SESSION_ID = `clientSessionId`;

	// NOTE: keep until Studio transition to Site-Builder is complete
	const LEGACY_MAP = {
		Generic: `GENERIC`,
		AdRoll: `ADROLL`,
		AdStage: `ADSTAGE`,
		Facebook: `FACEBOOK`,
		Feathr: `FEATHR`,
		Foundee: `FOUNDEE`,
		'Google AdWords': `GOOGLE_ADWORDS`,
		'Google Analytics': `GOOGLE_ANALYTICS`,
		'Google Analytics (GA4)': `GOOGLE_ANALYTICS_4`,
		Munchkin: `MUNCHKIN`,
		Partnerize: `PARTNERIZE`,
		Pinterest: `PINTEREST`,
		Riskified: `RISKIFIED`,
		Segment: `SEGMENT`,
		'Sift Science': `SIFT_SCIENCE`,
		Snapchat: `SNAPCHAT`,
		TikTok: `TIKTOK`,
		Twitter: `TWITTER`,
		'Vertical Mass': `VERTICAL_MASS`,
	};
	const PAYMENT_CHECKOUT_STEPS = [`payment`];
	const NEW_PAYMENT_STEPS = [`new-payment`];
	const CHECKOUT_STEPS = [
		`add-ons`,
		`login`,
		`guest`,
		`signup`,
		`birthday`,
		`custom-forms`,
		`shipping`,
		`order-protection`,
		`payment`,
		`new-single-payment`,
		`new-saved-payment`,
	];
	const USAGE_TYPES = {
		functional: `functional`,
		analytics: `analytics`,
		marketing: `marketing`,
	};
	const PURCHASE_FLOWS = {
		purchase: `Purchase`,
		upgrade: `Upgrade`,
		resale: `Resale`,
	};
	const EVENT_TYPE_MAP = {
		// defines which tracking config to load
		// config relates to the entries in the tracking array in the response from api/page/requirements
		'cart-add': { config: `cart_add`, handler: `onCartAdd` },
		'cart-remove': { config: `cart_remove`, handler: `onCartRemove` },
		'cart-view': { config: `conversion`, handler: `onCartView` },
		'checkout-init': { config: `cart_init`, handler: `onCheckoutInit` },
		'checkout-step': { config: `cart_checkout`, handler: `onCheckoutStep` },
		'checkout-step-complete': { config: `cart_checkout`, handler: `onCheckoutStepComplete` },
		'commerce-init': { config: `conversion`, handler: `onCommerceInit` },
		'product-list-view': { config: `conversion`, handler: `onProductListView` },
		'page-view': { config: `page`, handler: `onPageView` },
		'product-click': { config: `conversion`, handler: `onProductClick` },
		'product-view': { config: `conversion`, handler: `onProductView` },
		purchase: { config: `conversion`, handler: `onPurchase` },
		'social-share': { config: `share`, handler: `onSocialShare` },
		'upgrade-exit-form': { config: `conversion`, handler: `onUpgradeExitForm` },
		'upgrade-load-form': { config: `conversion`, handler: `onUpgradeLoadForm` },
		'upgrade-see-available': { config: `conversion`, handler: `onUpgradeSeeAvailable` },
		'upgrade-select-original-ticket': { config: `conversion`, handler: `onUpgradeSelectOriginalTicket` },
		'upgrade-deselect-original-ticket': { config: `conversion`, handler: `onUpgradeDeselectOriginalTicket` },
		'user-log-in': { config: `conversion`, handler: `onUserLogIn` },
		'user-sign-up': { config: `conversion`, handler: `onUserSignUp` },
		'wishlist-add': { config: `follow`, handler: `onWishlistAdd` },
		'wishlist-remove': { config: `unfollow`, handler: `onWishlistRemove` },
	};

	return Super.inherit({
		namespace,
		singleton: true,
		requires: [`System`, `Time`, `XHR`],
		prepare(setup) {
			// create new read-only map for caching registered providers
			this.defineProperty(`providers`, { value: {} });

			const { data, promises } = this;
			const firstPageView = true;
			const allowFunctional = false;
			const allowAnalytics = false;
			const allowMarketing = false;
			const trackingQueue = [];
			const trackingConfig = {};
			const random = Math.random();
			const uid = `r.${random}`;
			this.assign(data, {
				allowFunctional,
				allowAnalytics,
				allowMarketing,
				trackingQueue,
				firstPageView,
				trackingConfig,
				uid,
			});

			// used to chain api calls into sequence, to avoid issues loading the same api multiple times at once
			const providerActions = {};
			const providerApis = {};
			this.assign(promises, { providerApis, providerActions });
		},

		constructor(setup) {
			const { Time, Utils, XHR, data } = this;

			const customTrackingConfig = {
				cart_add: [],
				cart_init: [],
				cart_checkout: [],
				cart_remove: [],
				conversion: [],
				follow: [],
				page: [],
				share: [],
				unfollow: [],
			};

			data.customTrackingConfig = customTrackingConfig;

			// Register "Generic" provider, which is used for hitting arbitrary URLs with image / iframe loads
			this.register({
				makeCacheKey: null, // no caching
				provider: `GENERIC`,
				usageType: USAGE_TYPES.marketing,
				obtain: ({ id, params }) => {
					const { type = `image` } = params;

					// simulate an api that each hook can use
					return ({ url, replacements }) => {
						const randomNumber = Math.random();
						const random = `${randomNumber}`;
						const block = false; // no app blocking
						const force = true; // don't prevent duplicate calls
						let src = url;

						if (replacements === undefined) {
							replacements = { random };
						} else {
							replacements.random = random;
						}

						this.each(replacements, (value, key) => {
							const pattern = `::${key}::`;
							const regex = new RegExp(pattern, `g`); // global check
							const escapedValue = encodeURIComponent(value); // encode spaces, #, brackets, and so on

							src = src.replace(regex, escapedValue);
						});

						if (type === `iframe`) {
							return XHR.loadIframe({ block, force, src });
						} else if (type === `image`) {
							return XHR.loadImage({ block, force, src });
						} else {
							throw new Error(`Unrecognized Generic params.type: "${type}"`);
						}
					};
				},
				onCartAdd: (api, evt) => {
					const { App } = this;
					const { item, pageUrl, platform, productList, sessionId, targetUrl: url, uid, user } = evt;
					const { id: event_id, name: event_name } = productList;
					const { id: sale_id, name: sale_name, price: sale_price, quantity } = item;
					const userId = this.isObject(user) ? user.id : null;
					const email = this.isObject(user) ? user.emailHash : null;
					const phone = this.isObject(user) ? user.phoneHash : null;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					const replacements = {
						event_id,
						event_name,
						capi_event_id: uid,
						external_id: sessionId,
						fan_id: userId,
						fbclid: fbc,
						fbp,
						hash_email: email,
						hash_phone: phone,
						platform,
						quantity,
						sale_id,
						sale_name,
						sale_price,
						uid,
						url: pageUrl,
					};
					return api({ replacements, url });
				},
				onCartRemove: (api, evt) => {
					const { App } = this;
					const { item, platform, productList, pageUrl, sessionId, targetUrl: url, uid, user } = evt;
					const { id: event_id, name: event_name } = productList;
					const { id: sale_id, name: sale_name, price: sale_price, quantity } = item;
					const userId = this.isObject(user) ? user.id : null;
					const email = this.isObject(user) ? user.emailHash : null;
					const phone = this.isObject(user) ? user.phoneHash : null;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					const replacements = {
						event_id,
						event_name,
						capi_event_id: uid,
						external_id: sessionId,
						fan_id: userId,
						fbclid: fbc,
						fbp,
						hash_email: email,
						hash_phone: phone,
						platform,
						quantity,
						sale_id,
						sale_name,
						sale_price,
						uid,
						url: pageUrl,
					};
					return api({ replacements, url });
				},
				onCheckoutInit: (api, evt) => {
					const { App } = this;
					const { cart, platform, pageUrl, productList, sessionId, targetUrl: url, uid, user } = evt;
					const { quantity } = cart;
					const { id: event_id, name: event_name } = productList;
					const userId = this.isObject(user) ? user.id : null;
					const email = this.isObject(user) ? user.emailHash : null;
					const phone = this.isObject(user) ? user.phoneHash : null;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					const replacements = {
						event_id,
						event_name,
						capi_event_id: uid,
						external_id: sessionId,
						fan_id: userId,
						fbclid: fbc,
						fbp,
						hash_email: email,
						hash_phone: phone,
						platform,
						quantity,
						uid,
						url: pageUrl,
					};
					return api({ replacements, url });
				},
				onCheckoutStep: (api, evt) => {
					const { App } = this;
					const {
						pageUrl,
						platform,
						productList,
						quantity,
						sessionId,
						step: stepName,
						stepNumber: step,
						targetUrl: url,
						uid,
						user,
					} = evt;
					const { id: event_id, name: event_name } = productList;
					const userId = this.isObject(user) ? user.id : null;
					const email = this.isObject(user) ? user.emailHash : null;
					const phone = this.isObject(user) ? user.phoneHash : null;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					const replacements = {
						event_id,
						event_name,
						capi_event_id: uid,
						external_id: sessionId,
						fan_id: userId,
						fbclid: fbc,
						fbp,
						hash_email: email,
						hash_phone: phone,
						platform,
						quantity,
						step,
						stepName,
						uid,
						url: pageUrl,
					};
					return api({ replacements, url });
				},
				onPageView: (api, evt) => {
					const { App } = this;
					const { brand, pageUrl, platform, productList, sessionId, title: page_name, targetUrl: url, uid, user } = evt;
					const userId = this.isObject(user) ? user.id : null;
					const email = this.isObject(user) ? user.emailHash : null;
					const phone = this.isObject(user) ? user.phoneHash : null;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();
					const replacements = {
						capi_event_id: uid,
						external_id: sessionId,
						fan_id: userId,
						fbclid: fbc,
						fbp,
						hash_email: email,
						hash_phone: phone,
						page_name,
						platform,
						uid,
						url: pageUrl,
					};

					if (this.isObject(brand)) {
						this.assign(replacements, {
							group_name: brand.name,
						});
					}

					if (this.isObject(productList)) {
						this.assign(replacements, {
							currency: productList.currency,
							event_id: productList.id,
							event_name: productList.name,
						});
					}

					return api({ replacements, url });
				},
				onPurchase: (api, evt) => {
					const { App } = this;
					const { brand, cart, order, platform, productList, sessionId, targetUrl: url, uid, user } = evt;
					const userId = this.isObject(user) ? user.id : null;
					const email = this.isObject(user) ? user.emailHash : null;
					const phone = this.isObject(user) ? user.phoneHash : null;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					const replacements = {
						currency: productList.currency,
						event_id: productList.id,
						event_name: productList.name,
						capi_event_id: uid,
						external_id: sessionId,
						fan_id: userId,
						fbclid: fbc,
						fbp,
						group_id: brand.id,
						group_name: brand.name,
						hash_email: email,
						hash_phone: phone,
						order_amount: cart.total,
						order_id: order.id,
						order_quantity: cart.quantity,
						platform,
						promo_code: cart.promoCode,
						uid: evt.uid,
						url: evt.pageUrl,
					};
					return api({ replacements, url });
				},
				onSocialShare: (api, evt) => {
					const { App } = this;
					const {
						contentType: content_type,
						id: content_id,
						platform,
						pageUrl,
						sessionId,
						targetUrl: url,
						uid,
						user,
					} = evt;
					const userId = this.isObject(user) ? user.id : null;
					const email = this.isObject(user) ? user.emailHash : null;
					const phone = this.isObject(user) ? user.phoneHash : null;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					const replacements = {
						content_id,
						content_type,
						capi_event_id: uid,
						external_id: sessionId,
						fan_id: userId,
						fbclid: fbc,
						fbp,
						hash_email: email,
						hash_phone: phone,
						platform,
						uid,
						url: pageUrl,
					};
					return api({ replacements, url });
				},
				onWishlistAdd: (api, evt) => {
					const { App } = this;
					const { pageUrl, platform, productList, sessionId, targetUrl: url, type: content_type, uid, user } = evt;
					const { id: content_id } = productList;
					const userId = this.isObject(user) ? user.id : null;
					const email = this.isObject(user) ? user.emailHash : null;
					const phone = this.isObject(user) ? user.phoneHash : null;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					const replacements = {
						content_id,
						content_type,
						capi_event_id: uid,
						external_id: sessionId,
						fan_id: userId,
						fbclid: fbc,
						fbp,
						hash_email: email,
						hash_phone: phone,
						platform,
						type: content_type,
						url: pageUrl,
						uid,
					};
					return api({ replacements, url });
				},
				onWishlistRemove: (api, evt) => {
					const { App } = this;
					const { pageUrl, platform, productList, sessionId, targetUrl: url, type: content_type, uid, user } = evt;
					const { id: content_id } = productList;
					const userId = this.isObject(user) ? user.id : null;
					const email = this.isObject(user) ? user.emailHash : null;
					const phone = this.isObject(user) ? user.phoneHash : null;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					const replacements = {
						content_id,
						content_type,
						capi_event_id: uid,
						external_id: sessionId,
						fan_id: userId,
						fbclid: fbc,
						fbp,
						hash_email: email,
						hash_phone: phone,
						platform,
						url: pageUrl,
						uid,
					};
					return api({ replacements, url });
				},
			});

			// AdRoll integration
			this.register({
				provider: `ADROLL`,
				usageType: USAGE_TYPES.marketing,
				obtain: ({ id }) => {
					// create a custom api for the handler functions to use
					return async (options = {}) => {
						// base url prefix on protocol
						const prefix = document.location.protocol.indexOf(`https`) === 0 ? `//s.` : `//a.`;
						const keys = [
							`adroll_adv_id`,
							`adroll_pix_id`,
							`adroll_conversion_value`,
							`adroll_currency`,
							`adroll_email`,
						];
						const usedKeys = this.filter(keys, (key) => {
							const value = options[key];

							if (this.isDefined(value)) {
								window[key] = value;
								return true;
							}
						});

						const deps = [`${prefix}adroll.com/j/roundtrip.js`];
						await this.require({ deps }); // run the provided script
						const duration = 3000; // 3 seconds
						await this.defer({ duration }); // give the script a buffer to run

						// delete the global scope variables
						this.each(usedKeys, (key) => {
							if (window[key] === options[key]) {
								// delete the value if it still matches
								delete window[key];
							}
						});
					};
				},
				onPageView: (adroll, evt) => {
					const { params, platform } = evt;
					const { adroll_adv_id, adroll_pix_id } = params;

					return adroll({ adroll_adv_id, adroll_pix_id, platform });
				},
				onPurchase: (adroll, evt) => {
					const { cart, params, platform, productList } = evt;
					const { currency: adroll_currency } = productList;
					const { adroll_adv_id, adroll_pix_id } = params;
					const { total: adroll_conversion_value } = cart;

					return adroll({ adroll_adv_id, adroll_pix_id, adroll_conversion_value, adroll_currency, platform });
				},
				onSocialShare: (adroll, evt) => {
					const { params, platform } = evt;
					const { adroll_adv_id, adroll_pix_id } = params;

					return adroll({ adroll_adv_id, adroll_pix_id, platform });
				},
				onWishlistAdd: (adroll, evt) => {
					const { params, platform } = evt;
					const { adroll_adv_id, adroll_pix_id } = params;

					return adroll({ adroll_adv_id, adroll_pix_id, platform });
				},
				onWishlistRemove: (adroll, evt) => {
					const { params, platform } = evt;
					const { adroll_adv_id, adroll_pix_id } = params;

					return adroll({ adroll_adv_id, adroll_pix_id, platform });
				},
			});

			// AdStage integration
			this.register({
				provider: `ADSTAGE`,
				usageType: USAGE_TYPES.marketing,
				obtain: async ({ id }) => {
					await this.require({
						deps: [`//assets.adstage.io/analytics.js`],
					});
					const { _as: as } = window;

					as(`create`, id);
					return as;
				},
				onPageView: (as, evt) => as(`send`, `pageview`),
				onPurchase: (as, evt) => as(`send`, `pageview`),
			});

			// get or generate FBC (Facebook Click ID) parameter
			const getFBC = (fbclid) => {
				const cookieValue = this.App.getCookie(`_fbc`);
				return cookieValue || generateFBC(fbclid);
			};

			// generate FBC if not found
			const generateFBC = (fbclid) => {
				if (!fbclid) return null;
				const timestamp = Time.getTime(); // Current timestamp in milliseconds
				const fbc = `fb.1.${timestamp}.${fbclid}`;

				if (this.App.getCookie(`allow_marketing_cookies`) !== `1`) {
					this.log({ message: `Insights: generateFBC(): Marketing cookies are disabled` });

					return null;
				}

				this.App.setCookie(`_fbc`, fbc, 90);

				return fbc;
			};

			// get or generate FBP (Facebook Browser ID) parameter
			const getFBP = () => {
				const cookieValue = this.App.getCookie(`_fbp`);
				return cookieValue || generateFBP();
			};

			// generate FBP if not found
			const generateFBP = () => {
				const version = `fb.1.`;
				const timestamp = Math.floor(Time.getTime() / 1000); // Current Unix time in seconds
				const randomNumber = Math.random().toString(36).substring(2, 15); // Random session ID
				const fbp = version + timestamp + `.` + randomNumber;

				if (this.App.getCookie(`allow_marketing_cookies`) !== `1`) {
					this.log({ message: `Insights: generateFBP(): Marketing cookies are disabled` });

					return null;
				}

				this.App.setCookie(`_fbp`, fbp, 90);

				return fbp;
			};

			// Facebook Pixel integration
			this.register({
				provider: `FACEBOOK`,
				usageType: USAGE_TYPES.functional,
				obtain: async ({ id, sessionId }) => {
					let fbq = function () {
						const { callMethod, queue } = fbq;

						if (callMethod) {
							callMethod.apply(fbq, arguments);
						} else {
							queue.push(arguments);
						}
					};

					this.assign(fbq, { push: fbq, loaded: false, version: `2.0`, queue: [] });
					window._fbq = window.fbq = fbq;

					// load their script tag
					await this.require({ deps: [`//connect.facebook.net/en_US/fbevents.js`] });

					fbq = window.fbq; // refresh in case the reference has changed
					fbq(`init`, id, { external_id: sessionId });

					return (event, options, extraOptions) => {
						return fbq(`trackSingle`, id, event, options, extraOptions);
					};
				},
				onCartAdd: (fbq, evt) => {
					const { App } = this;
					const { custom, item, params, platform, productList, sessionId, uid } = evt;
					const { price } = item;
					const { currency, id: productListId, name: productListName } = productList;
					const content_type = `product`;
					const fbqOptions = { content_type, currency, event: productListName, platform, value: price };
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();
					const extraOptions = { eventID: uid, fbp, fbclid: fbc, external_id: sessionId };

					this.assign(fbqOptions, custom);

					if (params.content_type === `product_group`) {
						// each sale is being tracked
						const { product } = evt;
						const { id: productId } = product;
						const { name } = item;

						fbqOptions.content_name = `${productListName}-${name}`;
						fbqOptions.content_ids = [productId];
					} else {
						// event is being tracked as a whole
						fbqOptions.content_name = productListName;
						fbqOptions.content_ids = [productListId];
					}

					return fbq(`AddToCart`, fbqOptions, extraOptions);
				},
				onCheckoutInit: (fbq, evt) => {
					const { App } = this;
					const { cart, custom, params, platform, productList, sessionId, uid } = evt;
					const { category: content_category, currency, id: productListId, name: productListName } = productList;
					const { items, quantity: cartQuantity, total: value } = cart;
					const contents = [];
					const content_ids = [];
					const content_type = `product`;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					if (params.content_type === `product_group`) {
						this.each(items, (item) => {
							contents.push({
								id: item.id,
								quantity: item.quantity,
							});
							content_ids.push(item.id);
						});
					} else {
						// track the event as a single product
						contents.push({
							id: productListId,
							quantity: cartQuantity,
						});
						content_ids.push(productListId);
					}

					const fbqOptions = {
						content_ids,
						content_category,
						content_type,
						contents,
						currency,
						event: productListName,
						num_items: cartQuantity,
						platform,
						value,
					};

					this.assign(fbqOptions, custom);

					const extraOptions = { eventID: uid, fbp, fbclid: fbc, external_id: sessionId };

					return fbq(`InitiateCheckout`, fbqOptions, extraOptions);
				},
				onCommerceInit: (fbq, evt) => {
					/* load the api immediately */
				},
				onProductListView: (fbq, evt) => {
					const { App } = this;
					const { custom, params, platform, productList, sessionId, uid } = evt;
					const { currency, id: productListId, name: content_name } = productList;
					const content_ids = [productListId];
					const fbqOptions = { content_ids, content_name, currency, event: content_name, platform };
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();
					const extraOptions = { eventID: uid, fbp, fbclid: fbc, external_id: sessionId };

					this.assign(fbqOptions, custom);

					if (params.content_type === `product_group`) {
						// granular behavior, tracks each sale
						fbqOptions.content_type = `product_group`;
					} else {
						// default behavior, treats the whole event as the product
						fbqOptions.content_type = `product`;
					}

					return fbq(`ViewContent`, fbqOptions, extraOptions);
				},
				onPageView: (fbq, evt) => {
					const { App } = this;
					const { custom, platform, sessionId, uid } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();
					const fbqOptions = { platform };
					const extraOptions = { eventID: uid, fbp, fbclid: fbc, external_id: sessionId };

					this.assign(fbqOptions, custom);

					return fbq(`PageView`, fbqOptions, extraOptions);
				},
				onProductView: (fbq, evt) => {
					const { App } = this;
					const { params, sessionId } = evt;
					const { content_type } = params;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					if (content_type !== `product_group`) {
						// productList is being tracked as a whole, no handling for this case
						return;
					}

					const { custom, platform, product, productList, uid } = evt;
					const { id: productId, name: productName, price: value } = product;
					const { currency, name: productListName } = productList;
					const fbqOptions = {
						content_ids: [productId],
						content_name: `${productListName}-${productName}`,
						content_type: `product`,
						currency,
						event: productListName,
						platform,
						value,
					};
					const extraOptions = { eventID: uid, fbp, fbclid: fbc, external_id: sessionId };

					this.assign(fbqOptions, custom);

					// each sale is being tracked, log with FB
					return fbq(`ViewContent`, fbqOptions, extraOptions);
				},
				onPurchase: (fbq, evt) => {
					const { App } = this;
					const { cart, custom, params, platform, productList, sessionId, uid } = evt;
					const { currency, id: productListId, name: productListName } = productList;
					const { items, quantity: cartQuantity, total: value } = cart;
					const contents = [];
					const content_ids = [];
					const content_type = `product`;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					if (params.content_type === `product_group`) {
						// sales are being tracked individually
						this.each(items, (item) => {
							contents.push({
								id: item.id,
								quantity: item.quantity,
							});
							content_ids.push(item.id);
						});
					} else {
						// track the event as a single product
						contents.push({
							id: productListId,
							quantity: cartQuantity,
						});
						content_ids.push(productListId);
					}

					const fbqOptions = {
						content_ids,
						content_type,
						contents,
						currency,
						event: productListName,
						num_items: cartQuantity,
						platform,
						value,
					};
					const extraOptions = { eventID: uid, fbp, fbclid: fbc, external_id: sessionId };

					this.assign(fbqOptions, custom);

					return fbq(`Purchase`, fbqOptions, extraOptions);
				},
				onUserLogIn: (fbq, evt) => {
					const { App } = this;
					const { id: providerId, sessionId, user } = evt;
					const {
						birthday = ``,
						email = ``,
						firstName = ``,
						gender = ``,
						id: userId = ``,
						lastName = ``,
						phoneNumber = ``,
						postalCode = ``,
					} = user || {};
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();

					if (fbq.instance && fbq.instance.pixelsByID && fbq.instance.pixelsByID[providerId]) {
						const userData = (fbq.instance.pixelsByID[providerId].userData =
							fbq.instance.pixelsByID[providerId].userData || {});
						userData.external_id = userId || sessionId;
						userData.db = birthday;
						userData.em = email;
						userData.fn = firstName;
						userData.ge = gender;
						userData.ln = lastName;
						userData.ph = phoneNumber;
						userData.zp = postalCode;
					}

					return fbq(`UserLogIn`, { external_id: sessionId || userId, fbp, fbclid: fbc });
				},
				onUserSignUp: (fbq, evt) => {
					const { App } = this;
					const { id: providerId, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();
					const {
						birthday = ``,
						email = ``,
						firstName = ``,
						gender = ``,
						id: userId = ``,
						lastName = ``,
						phoneNumber = ``,
						postalCode = ``,
					} = user || {};

					if (fbq.instance && fbq.instance.pixelsByID && fbq.instance.pixelsByID[providerId]) {
						const userData = (fbq.instance.pixelsByID[providerId].userData =
							fbq.instance.pixelsByID[providerId].userData || {});
						userData.external_id = userId || sessionId;
						userData.db = birthday;
						userData.em = email;
						userData.fn = firstName;
						userData.ge = gender;
						userData.ln = lastName;
						userData.ph = phoneNumber;
						userData.zp = postalCode;
					}

					return fbq(`UserSignUp`, { external_id: sessionId || userId, fbp, fbclid: fbc });
				},
				onWishlistAdd: (fbq, evt) => {
					const { App } = this;
					const { custom, platform, productList, sessionId, uid } = evt;
					const { category: content_category, currency, id, name: content_name } = productList;
					const content_ids = [id];
					const fbqOptions = { content_category, content_ids, content_name, event: content_name, currency, platform };
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const fbc = getFBC(fbclid);
					const fbp = getFBP();
					const extraOptions = { eventID: uid, fbp, fbclid: fbc, external_id: sessionId };

					this.assign(fbqOptions, custom);

					fbq(`AddToWishlist`, fbqOptions, extraOptions);
					return fbq(`Lead`, fbqOptions, extraOptions);
				},
			});

			// Feathr integration
			this.register({
				provider: `FEATHR`,
				usageType: USAGE_TYPES.marketing,
				obtain: async ({ id, params }) => {
					const deps = [`//cdn.feathr.co/js/boomerang.min.js`];
					await this.require({ deps });
					const { feathr } = window;

					feathr(`fly`, id);
					return feathr;
				},
				onPageView: (feathr, evt) => {
					const { params, platform, title } = evt;
					var dictionary = { platform, title };

					this.each(params, (value, key) => {
						var split = value.split(`,`);
						dictionary[key] = split.length > 1 ? split : key;
					});

					return feathr(`sprinkle`, `page_view`, dictionary);
				},
				onPurchase: (feathr, evt) => {
					const { cart, order, params, platform, productList } = evt;
					const { currency } = productList;
					const { quantity, total } = cart;
					const { id: order_id } = order;
					const dictionary = { currency, order_id, platform, quantity, total };

					this.each(params, (value, key) => {
						const split = value.split(`,`);
						dictionary[key] = split.length > 1 ? split : key;
					});

					return feathr(`sprinkle`, `purchase`, dictionary);
				},
			});

			// Foundee integration
			this.register({
				provider: `FOUNDEE`,
				usageType: USAGE_TYPES.marketing,
				obtain: async ({ id, params }) => {
					/**
					 * Adapted from Foundee JS snippet:
					 *
					 * ! function(e, t, n, s, a, c, p, i, o, u) {
					 *     e[a] || ((i = e[a] = function() {
					 *         i.process ? i.process.apply(i, arguments) : i.queue.push(arguments)
					 *     }).queue = [],
					 *     i.pixelId = "3b8abb28-6c06-401a-a1de-dc0b0ec09584",
					 *     i.t = 1 * new Date, (o = t.createElement(n)).async = 1,
					 *     o.src = "https://found.ee/dmp/pixel.js?t=" + 864e5 * Math.ceil(new Date / 864e5),
					 *     (u = t.getElementsByTagName(n)[0]).parentNode.insertBefore(o, u))
					 * }(window, document, "script", 0, "foundee");
					 * foundee('', 'Y');
					 */

					let { foundee } = window;

					if (!foundee) {
						let _foundee = function () {
							if (_foundee.process) {
								_foundee.process.apply(_foundee, arguments);
							} else {
								_foundee.queue.push(arguments);
							}
						};
						_foundee.queue = [];
						_foundee.pixelId = id;
						_foundee.t = Date.now();
						foundee = window.foundee = _foundee;
					}

					const urlSuffix = 864e5 * Math.ceil(Date.now() / 864e5);
					const deps = [`https://found.ee/dmp/pixel.js?t=${urlSuffix}`];
					await this.require({ deps });

					foundee(``, `Y`);
					return foundee;
				},
				onPageView: (api, evt) => {
					/* api was loaded with provided id, done */
				},
				onPurchase: (api, evt) => {
					/* api was loaded with provided id, done */
				},
			});

			// Google Tag Manager is used for multiple integrations
			const obtainGoogleTagManager = async ({ id, params }) => {
				if (window.gtag === undefined) {
					window.dataLayer = window.dataLayer || [];
					window.gtag = function gtag() {
						window.dataLayer.push(arguments);
					};
					window.gtag(`js`, new Date());
				}

				const deps = [`https://www.googletagmanager.com/gtag/js`];
				await this.require({ deps });

				const { gtag } = window;
				const send_page_view = false; // we manually track page_view events

				gtag(`config`, id, { send_page_view });

				// return a version of the api that embeds the id in the tracking parameters
				return (eventName, params = {}) => {
					if (!this.isString(params.send_to)) {
						params.send_to = id;
					}

					return gtag(`event`, eventName, params);
				};
			};
			// TXP-8406: GA4/AdWords collect endpoint rejects oversized hits (HTTP 413). A 200-item
			// cap alone isn't sufficient (verified against the incident: 214 items ~= 33KB, so 200
			// items of similar content would still be ~30KB) so we also enforce a byte budget.
			const MAX_ITEM_LIST_ITEMS = 200;
			const MAX_ITEM_LIST_BYTES = 8000;
			const capItemListForTransport = (items) => {
				if (!this.isArray(items, 1)) {
					return items;
				}

				let capped = items.length > MAX_ITEM_LIST_ITEMS ? items.slice(0, MAX_ITEM_LIST_ITEMS) : items;
				const totalBytes = JSON.stringify(capped).length;

				if (totalBytes > MAX_ITEM_LIST_BYTES) {
					const avgBytesPerItem = totalBytes / capped.length;
					capped = capped.slice(0, Math.max(1, Math.floor(MAX_ITEM_LIST_BYTES / avgBytesPerItem)));
				}

				if (capped.length < items.length) {
					this.log({
						type: `warning`,
						message: `Truncated ecommerce items array from ${items.length} to ${capped.length} to satisfy GA4/AdWords payload limits`,
					});
				}

				return capped;
			};
			const makeGoogleTagManagerProductList = ({ brand, eventTitle, items, purchaseFlowItem }) => {
				if (!this.isArray(items, 1)) {
					this.log(`No items provided to makeGoogleTagManagerProductList`, items);
					return null;
				}
				if (!this.isObject(brand)) {
					this.log(`No brand provided to makeGoogleTagManagerProductList`, brand);
					return null;
				}

				const { name: item_brand } = brand;
				const cappedItems = capItemListForTransport(items);

				return this.map(cappedItems, (item) => {
					const { category: item_category, custom, id: item_id, name: item_name, price, quantity } = item;
					const result = {
						item_brand,
						item_category,
						item_id,
						item_name: eventTitle ? `${item_name} - ${eventTitle}` : item_name,
						price,
						quantity,
						purchase_flow_item: purchaseFlowItem,
					};
					this.assign(result, custom);

					return result;
				});
			};

			// Google AdWords integration
			// NOTE: implementation is the same as GA4, with the addition of "conversion" tracking in onPurchase
			this.register({
				provider: `GOOGLE_ADWORDS`,
				usageType: USAGE_TYPES.analytics,
				obtain: obtainGoogleTagManager,
				onCartAdd: (gtag_event, evt) => {
					const { brand, custom, item, platform, productList } = evt;
					const { currency, name: item_list_name } = productList;
					const { value } = item;
					const items = makeGoogleTagManagerProductList({ brand, eventTitle: item_list_name, items: [item] });
					const trackingParams = { currency, items, platform, value };

					this.assign(trackingParams, custom);
					return gtag_event(`add_to_cart`, trackingParams);
				},
				onCartRemove: (gtag_event, evt) => {
					const { brand, custom, item, platform, productList } = evt;
					const { currency, name: item_list_name } = productList;
					const { value } = item;
					const items = makeGoogleTagManagerProductList({ brand, eventTitle: item_list_name, items: [item] });
					const trackingParams = { currency, items, platform, value };

					this.assign(trackingParams, custom);
					return gtag_event(`remove_from_cart`, trackingParams);
				},
				onCheckoutInit: (gtag_event, evt) => {
					const { brand, cart, custom, platform, productList } = evt;
					const { currency, name: item_list_name } = productList;
					const { total: value } = cart;
					const items = makeGoogleTagManagerProductList({ brand, eventTitle: item_list_name, items: cart.items });
					const trackingParams = { currency, items, platform, value };

					// NOTE: "coupon" is a recommended value, but not possible to enter promo until checkout
					this.assign(trackingParams, custom);
					return gtag_event(`begin_checkout`, trackingParams);
				},
				onCartView: (gtag_event, evt) => {
					const { brand, cart, custom, platform, productList } = evt;
					const { currency, name: item_list_name } = productList;
					const { total: value } = cart;
					const items = makeGoogleTagManagerProductList({ brand, eventTitle: item_list_name, items: cart.items });
					const trackingParams = { currency, items, platform, value };

					this.assign(trackingParams, custom);
					return gtag_event(`view_cart`, trackingParams);
				},
				onCheckoutStepComplete: (gtag_event, evt) => {
					const { brand, cart, custom, platform, productList, step } = evt;
					const { promoCode, total: value } = cart;
					const { currency, name: item_list_name } = productList;
					const items = makeGoogleTagManagerProductList({ brand, eventTitle: item_list_name, items: cart.items });
					const trackingParams = { currency, items, platform, value };

					if (this.isString(promoCode, 1)) {
						trackingParams.coupon = promoCode;
					}

					this.assign(trackingParams, custom);

					if (
						this.includes(PAYMENT_CHECKOUT_STEPS, step) || // existing payment method
						this.includes(NEW_PAYMENT_STEPS, step) // new payment method
					) {
						return gtag_event(`add_payment_info`, trackingParams);
					} else if (step === `shipping`) {
						return gtag_event(`add_shipping_info`, trackingParams);
					}
				},
				onProductListView: (gtag_event, evt) => {
					const { brand, custom, platform, productList } = evt;
					const { id: item_list_id, name: item_list_name, products } = productList;
					const { name: item_brand } = brand;
					const cappedProducts = capItemListForTransport(products);
					const items = this.map(cappedProducts, (product) => ({
						item_brand,
						item_id: product.id,
						item_name: `${product.name} - ${item_list_name}`,
						item_category: product.item_category,
						price: product.price,
						quantity: 0, // previously 1?
					}));
					const trackingParams = { item_list_id, item_list_name, items, platform };

					this.assign(trackingParams, custom);
					return gtag_event(`view_item_list`, trackingParams);
				},
				onPageView: (gtag_event, evt) => {
					const { custom, pageUrl: page_location, platform, title: page_title } = evt;
					const trackingParams = { page_location, page_title, platform };

					this.assign(trackingParams, custom);
					return gtag_event(`page_view`, trackingParams);
				},
				onProductView: (gtag_event, evt) => {
					const { brand, custom, platform, product, productList } = evt;
					const { name: item_brand } = brand;
					const { currency, name: item_list_name } = productList;
					const { category: item_category, id: item_id, name: item_name, price } = product;
					const quantity = 1;
					const items = [
						{ item_brand, item_category, item_id, item_name: `${item_name} - ${item_list_name}`, price, quantity },
					];
					const value = price;
					const trackingParams = { currency, items, platform, value };

					this.assign(trackingParams, custom);
					return gtag_event(`view_item`, trackingParams);
				},
				onPurchase: (gtag_event, evt) => {
					const { brand, cart, custom, id: providerId, order, params, platform, productList } = evt;
					const { promoCode, shipping, total: value } = cart;
					const { id: transaction_id } = order;
					const { currency, name: item_list_name } = productList;
					const { label: providerLabel } = params;
					const items = makeGoogleTagManagerProductList({ brand, eventTitle: item_list_name, items: cart.items });
					const trackingParams = { currency, items, platform, shipping, transaction_id, value };

					if (this.isString(promoCode)) {
						trackingParams.coupon = promoCode;
					}

					let send_to = providerId;
					if (this.isString(providerLabel, 1)) {
						send_to += `/${providerLabel}`;
					}
					gtag_event(`conversion`, { currency, send_to, value });

					this.assign(trackingParams, custom);
					return gtag_event(`purchase`, trackingParams);
				},
				onSocialShare: (gtag_event, evt) => {
					const { custom, type: content_type, platform, productList, method } = evt;
					const { id: item_id } = productList;
					const trackingParams = { content_type, item_id, method, platform };

					this.assign(trackingParams, custom);
					return gtag_event(`share`, trackingParams);
				},
				onUserLogIn: (gtag_event, evt) => {
					const { custom, method, platform } = evt;
					const trackingParams = { method, platform };

					this.assign(trackingParams, custom);
					return gtag_event(`login`, trackingParams);
				},
				onUserSignUp: (gtag_event, evt) => {
					const { custom, method, platform } = evt;
					const trackingParams = { method, platform };

					this.assign(trackingParams, custom);
					return gtag_event(`sign_up`, trackingParams);
				},
				onWishlistAdd: (gtag_event, evt) => {
					const { custom, platform, productList } = evt;
					const { currency, name: item_name, id: item_id } = productList;
					const trackingParams = { currency, platform };

					this.assign(trackingParams, custom);
					// NOTE: documentation wants "value", but has no meaning in this context
					gtag_event(`generate_lead`, trackingParams);

					const items = [{ item_id, item_name }];
					this.assign(trackingParams, { items });
					return gtag_event(`add_to_wishlist`, trackingParams);
				},
			});

			// Google Analytics (UA) integration
			this.register({
				provider: `GOOGLE_ANALYTICS`,
				usageType: USAGE_TYPES.analytics,
				obtain: async ({ id, params }) => {
					const deps = [`//www.google-analytics.com/analytics.js`];
					const [ga] = await this.require({ deps });
					const { domain } = params;

					// use a custom name for capturing against each account, Tixr0, Tixr1, etc.
					const newId = this.uniqueId();
					const name = `Tixr${newId}`;
					const allowLinker = true;

					// initialize with the custom name
					ga(`create`, id, `auto`, { name, allowLinker });

					if (this.isString(domain, 1)) {
						const domains = domain.split(`,`);

						ga(`require`, `linker`);
						ga(`linker:autoLink`, domains);
					}

					// use the enhanced commerce plugin
					ga(`require`, `ec`);

					function bound_ga() {
						// a proxy version of the ga object, that uses Tixr0.____, binding its commands to the appropriate version
						const args = Utils.toArray(arguments);
						const [first] = arguments;

						args[0] = `${name}.${first}`;
						return ga.apply(this, args);
					}

					bound_ga(`require`, `displayfeatures`);

					return bound_ga;
				},
				onCartAdd: (ga, evt) => {
					const { item, platform, productList } = evt;
					const { currency, name: productListName } = productList;
					const { id: productId, name: productName, price, quantity } = item;
					const name = `${productListName}-${productName}`;

					ga(`ec:addProduct`, { currency, id: productId, name, price, quantity, platform });
					ga(`ec:setAction`, `add`); // add product to shopping cart
					ga(`send`, `event`, `UX`, `click`, `add to cart`);
				},
				onCartRemove: (ga, evt) => {
					const { item, platform, productList } = evt;
					const { currency, name: productListName } = productList;
					const { id: productId, name: productName, price, quantity } = item;
					const name = `${productListName}-${productName}`;

					ga(`ec:addProduct`, { currency, id: productId, name, price, quantity, platform });
					ga(`ec:setAction`, `remove`);
					ga(`send`, `event`, `UX`, `click`, `remove from cart`);
				},
				onCheckoutInit: (ga, evt) => {
					// first step
					const { platform, productList } = evt;
					const { currency } = productList;

					ga(`ec:setAction`, `checkout`, { currency, platform, step: 1 });
					return ga(`send`, `event`, `UX`, `click`, `checkout step 1`);
				},
				onCheckoutStep: (ga, evt) => {
					const { platform, productList, stepNumber: step } = evt;
					const { currency } = productList;

					ga(`ec:setAction`, `checkout`, { currency, platform, step });
					ga(`send`, `event`, `UX`, `click`, `cart checkout`);
				},
				onCommerceInit: (ga, evt) => {
					/* load api immediately */
				},
				onPageView: (ga, { pageUrl, path, title }) =>
					ga(`send`, {
						hitType: `pageview`,
						location: pageUrl,
						page: path,
						title,
					}),
				onPurchase: (ga, evt) => {
					const { brand, cart, order, platform, productList } = evt;
					const { currency, name: productListName } = productList;
					const { items, shipping, total: revenue } = cart;
					const { name: affiliation } = brand;
					const { id: orderId } = order;
					const tax = ``;

					this.each(items, (item) => {
						const { id: productId, name: itemName, price, quantity } = item;
						const name = `${productListName}-${itemName}`;

						ga(`ec:addProduct`, { currency, id: productId, name, price, quantity });
					});

					ga(`ec:setAction`, `purchase`, { affiliation, currency, id: orderId, platform, revenue, shipping, tax });
					ga(`send`, `event`, `UX`, `click`, `order successful`);
				},
				onSocialShare: (ga, evt) => {
					const { method, platform, productList, type } = evt;
					const { id: eventValue } = productList;
					const eventAction = `share-${type}-${method}`; // e.g. event-share-facebook, group-share-twitter
					const eventCategory = `user-action`;
					return ga(`send`, `event`, { eventAction, eventCategory, eventValue, platform });
				},
				onWishlistAdd: (ga, evt) => {
					const { platform, productList, type } = evt;
					const { id: eventValue } = productList;

					const eventAction = `follow-${type}`;
					const eventCategory = `user-action`;
					return ga(`send`, `event`, { eventAction, eventCategory, eventValue, platform });
				},
				onWishlistRemove: (ga, evt) => {
					const { platform, productList, type } = evt;
					const { id: eventValue } = productList;

					const eventAction = `unfollow-${type}`;
					const eventCategory = `user-action`;
					return ga(`send`, `event`, { eventAction, eventCategory, eventValue, platform });
				},
			});

			const getTixrId = (custom = {}) => {
				return this.isString(custom.tixr_id) ? custom.tixr_id : null; // e.g. `u8126`
			};

			// Google Analytics 4 integration
			this.register({
				provider: `GOOGLE_ANALYTICS_4`,
				usageType: USAGE_TYPES.analytics,
				obtain: obtainGoogleTagManager,
				onCartAdd: (gtag_event, evt) => {
					const {
						brand,
						platform,
						custom,
						item,
						orderProtectionFee,
						paymentPlanFee,
						productList,
						purchaseFlow,
						purchaseFlowItem,
					} = evt;
					const { id: group_id } = brand;
					const { currency, id: event_id, name: item_list_name } = productList;
					const { value } = item;
					const items = makeGoogleTagManagerProductList({
						brand,
						eventTitle: item_list_name,
						items: [item],
						purchaseFlowItem,
					});
					const userId = getTixrId(custom);
					const trackingParams = {
						platform,
						currency,
						tixr_event_id: event_id,
						group_id,
						items,
						price_order_protection: orderProtectionFee,
						price_payment_plan: paymentPlanFee,
						purchase_flow: purchaseFlow,
						purchase_flow_item: purchaseFlowItem,
						value,
						user_id: userId,
					};

					this.assign(trackingParams, custom);
					return gtag_event(`add_to_cart`, trackingParams);
				},
				onCartRemove: (gtag_event, evt) => {
					const {
						brand,
						platform,
						custom,
						item,
						productList,
						paymentPlanFee,
						purchaseFlow,
						purchaseFlowItem,
						orderProtectionFee,
					} = evt;
					const { id: group_id } = brand;
					const { currency, id: event_id, name: item_list_name } = productList;
					const { value } = item;
					const items = makeGoogleTagManagerProductList({
						brand,
						eventTitle: item_list_name,
						items: [item],
						purchaseFlowItem,
					});
					const userId = getTixrId(custom);
					const trackingParams = {
						platform,
						currency,
						tixr_event_id: event_id,
						group_id,
						items,
						price_order_protection: orderProtectionFee,
						price_payment_plan: paymentPlanFee,
						purchase_flow: purchaseFlow,
						purchase_flow_item: purchaseFlowItem,
						value,
						user_id: userId,
					};

					this.assign(trackingParams, custom);
					return gtag_event(`remove_from_cart`, trackingParams);
				},
				onCheckoutInit: (gtag_event, evt) => {
					const { brand, platform, cart, custom, productList, purchaseFlow } = evt;
					const { id: group_id } = brand;
					const { currency, id: event_id, name: item_list_name } = productList;
					const { total: value, paymentPlanFee, orderProtection } = cart;
					const items = makeGoogleTagManagerProductList({
						brand,
						eventTitle: item_list_name,
						items: cart.items,
						purchaseFlowItem: purchaseFlow,
					});
					const orderProtectionQuote = this.isObject(orderProtection) ? orderProtection.quote : 0;
					const userId = getTixrId(custom);
					const trackingParams = {
						platform,
						currency,
						tixr_event_id: event_id,
						group_id,
						items,
						price_order_protection: orderProtectionQuote,
						price_payment_plan: paymentPlanFee,
						purchase_flow: purchaseFlow,
						purchase_flow_item: purchaseFlow,
						value,
						user_id: userId,
					};

					// NOTE: "coupon" is a recommended value, but not possible to enter promo until checkout
					this.assign(trackingParams, custom);
					return gtag_event(`begin_checkout`, trackingParams);
				},
				onCartView: (gtag_event, evt) => {
					const { brand, platform, cart, custom, orderProtectionFee, paymentPlanFee, productList, purchaseFlow } = evt;
					const { id: group_id } = brand;
					const { currency, id: event_id, name: item_list_name } = productList;
					const { total: value } = cart;
					const items = makeGoogleTagManagerProductList({
						brand,
						eventTitle: item_list_name,
						items: cart.items,
						purchaseFlowItem: purchaseFlow,
					});
					const userId = getTixrId(custom);
					const trackingParams = {
						platform,
						currency,
						tixr_event_id: event_id,
						group_id,
						items,
						value,
						price_order_protection: orderProtectionFee,
						price_payment_plan: paymentPlanFee,
						purchase_flow: purchaseFlow,
						purchase_flow_item: purchaseFlow,
						user_id: userId,
					};

					this.assign(trackingParams, custom);
					return gtag_event(`view_cart`, trackingParams);
				},
				onCheckoutStepComplete: (gtag_event, evt) => {
					const { brand, platform, cart, custom, orderProtectionFee, paymentPlanFee, productList, purchaseFlow, step } =
						evt;
					const { id: group_id } = brand;
					const { promoCode, total: value } = cart;
					const { currency, id: event_id, name: item_list_name } = productList;
					const items = makeGoogleTagManagerProductList({
						brand,
						eventTitle: item_list_name,
						items: cart.items,
						purchaseFlowItem: purchaseFlow,
					});
					const userId = getTixrId(custom);
					const trackingParams = {
						platform,
						currency,
						tixr_event_id: event_id,
						group_id,
						items,
						price_order_protection: orderProtectionFee,
						price_payment_plan: paymentPlanFee,
						purchase_flow: purchaseFlow,
						purchase_flow_item: purchaseFlow,
						value,
						user_id: userId,
					};

					if (this.isString(promoCode, 1)) {
						trackingParams.coupon = promoCode;
					}

					this.assign(trackingParams, custom);

					if (
						this.includes(PAYMENT_CHECKOUT_STEPS, step) || // existing payment method
						this.includes(NEW_PAYMENT_STEPS, step) // new payment method
					) {
						return gtag_event(`add_payment_info`, trackingParams);
					} else if (step === `shipping`) {
						return gtag_event(`add_shipping_info`, trackingParams);
					}
				},
				onProductListView: (gtag_event, evt) => {
					const { brand, platform, custom, productList } = evt;
					const { id: item_list_id, name: item_list_name, products } = productList;
					const { id: group_id, name: item_brand } = brand;
					const userId = getTixrId(custom);
					const cappedProducts = capItemListForTransport(products);
					const items = this.map(cappedProducts, (product) => ({
						item_brand,
						item_id: product.id,
						item_name: `${product.name} - ${item_list_name}`,
						item_category: product.item_category,
						price: product.price,
						quantity: 0, // previously 1?
					}));
					const trackingParams = {
						item_list_id,
						event_id: item_list_id,
						group_id,
						item_list_name,
						items,
						platform,
						user_id: userId,
					};

					this.assign(trackingParams, custom);
					return gtag_event(`view_item_list`, trackingParams);
				},
				onPageView: (gtag_event, evt) => {
					const {
						platform,
						custom,
						pageUrl: page_location,
						title: page_title,
						orderProtectionFee,
						paymentPlanFee,
						purchaseFlow,
						purchaseFlowItem,
					} = evt;
					const userId = getTixrId(custom);
					const trackingParams = {
						platform,
						page_location,
						page_title,
						price_order_protection: orderProtectionFee,
						price_payment_plan: paymentPlanFee,
						purchase_flow: purchaseFlow,
						purchase_flow_item: purchaseFlowItem,
						user_id: userId,
					};

					this.assign(trackingParams, custom);
					return gtag_event(`page_view`, trackingParams);
				},
				onProductView: (gtag_event, evt) => {
					const { brand, platform, custom, product, productList } = evt;
					const { id: group_id, name: item_brand } = brand;
					const { currency, id: event_id, name: item_list_name } = productList;
					const { category: item_category, id: item_id, name: item_name, price } = product;
					const quantity = 1;
					const items = [
						{ item_brand, item_category, item_id, item_name: `${item_name} - ${item_list_name}`, price, quantity },
					];
					const value = price;
					const userId = getTixrId(custom);
					const trackingParams = {
						currency,
						tixr_event_id: event_id,
						group_id,
						items,
						value,
						platform,
						user_id: userId,
					};

					this.assign(trackingParams, custom);
					return gtag_event(`view_item`, trackingParams);
				},
				onPurchase: (gtag_event, evt) => {
					const {
						brand,
						platform,
						cart,
						custom,
						order,
						paymentPlanFee,
						productList,
						purchaseFlow,
						orderProtectionFee,
					} = evt;
					const { id: group_id } = brand;
					const { promoCode, shipping, total: value } = cart;
					const { id: transaction_id } = order;
					const { currency, id: event_id, name: item_list_name } = productList;
					const items = makeGoogleTagManagerProductList({
						brand,
						eventTitle: item_list_name,
						items: cart.items,
						purchaseFlowItem: purchaseFlow,
					});
					const userId = getTixrId(custom);
					const trackingParams = {
						platform,
						currency,
						tixr_event_id: event_id,
						group_id,
						items,
						price_order_protection: orderProtectionFee,
						price_payment_plan: paymentPlanFee,
						purchase_flow: purchaseFlow,
						purchase_flow_item: purchaseFlow,
						shipping,
						transaction_id,
						value,
						user_id: userId,
					};

					if (this.isString(promoCode)) {
						trackingParams.coupon = promoCode;
					}

					this.assign(trackingParams, custom);
					return gtag_event(`purchase`, trackingParams);
				},
				onSocialShare: (gtag_event, evt) => {
					const { platform, custom, type: content_type, productList, method } = evt;
					const { id: item_id } = productList;
					const userId = getTixrId(custom);
					const trackingParams = { content_type, item_id, method, platform, user_id: userId };

					this.assign(trackingParams, custom);
					return gtag_event(`share`, trackingParams);
				},
				onUpgradeDeselectOriginalTicket: (gtag_event, evt) => {
					const { custom, item_brand, item_category, item_id, item_name, platform, productType, quantity } = evt;
					const userId = getTixrId(custom);
					const trackingParams = {
						platform,
						item_brand,
						item_category,
						item_id,
						item_name,
						productType,
						quantity,
						purchase_flow_item: PURCHASE_FLOWS.upgrade,
						user_id: userId,
					};

					this.assign(trackingParams, custom);
					return gtag_event(`upgrade_deselect_original_ticket`, trackingParams);
				},
				onUpgradeExitForm: (gtag_event, evt) => {
					return gtag_event(`upgrade_exit_form`, { purchase_flow_item: PURCHASE_FLOWS.upgrade });
				},
				onUpgradeLoadForm: (gtag_event, evt) => {
					return gtag_event(`upgrade_load_form`, { purchase_flow_item: PURCHASE_FLOWS.upgrade });
				},
				onUpgradeSeeAvailable: (gtag_event, evt) => {
					return gtag_event(`upgrade_see_available`, { purchase_flow_item: PURCHASE_FLOWS.upgrade });
				},
				onUpgradeSelectOriginalTicket: (gtag_event, evt) => {
					const { custom, item_brand, item_category, item_id, item_name, platform, productType, quantity } = evt;
					const userId = getTixrId(custom);
					const trackingParams = {
						platform,
						item_brand,
						item_category,
						item_id,
						item_name,
						productType,
						quantity,
						purchase_flow_item: PURCHASE_FLOWS.upgrade,
						user_id: userId,
					};

					this.assign(trackingParams, custom);
					return gtag_event(`upgrade_select_original_ticket`, trackingParams);
				},
				onUserLogIn: (gtag_event, evt) => {
					const { custom, method, platform } = evt;
					const userId = getTixrId(custom);
					const trackingParams = { method, platform, user_id: userId };

					this.assign(trackingParams, custom);
					return gtag_event(`login`, trackingParams);
				},
				onUserSignUp: (gtag_event, evt) => {
					const { custom, method, platform } = evt;
					const userId = getTixrId(custom);
					const trackingParams = { method, platform, user_id: userId };

					this.assign(trackingParams, custom);
					return gtag_event(`sign_up`, trackingParams);
				},
				onWishlistAdd: (gtag_event, evt) => {
					const { platform, custom, productList } = evt;
					const { currency, name: item_name, id: item_id } = productList;
					const userId = getTixrId(custom);
					const trackingParams = { currency, platform, user_id: userId };

					this.assign(trackingParams, custom);
					// NOTE: documentation wants "value", but has no meaning in this context
					gtag_event(`generate_lead`, trackingParams);

					const items = [{ item_id, item_name }];
					this.assign(trackingParams, { items });
					return gtag_event(`add_to_wishlist`, trackingParams);
				},
			});

			// Munchkin integration
			this.register({
				provider: `MUNCHKIN`,
				usageType: USAGE_TYPES.marketing,
				obtain: async ({ id, params }) => {
					const deps = [`//munchkin.marketo.net/munchkin.js`];
					await this.require({ deps });
					const { Munchkin } = window;
					const asyncOnly = true;

					Munchkin.init(id, { asyncOnly });
					return Munchkin;
				},
				onPageView: (Munchkin, evt) => {
					const { pageUrl: url } = evt;
					return Munchkin.munchkinFunction(`visitWebPage`, { url });
				},
				onPurchase: async (Munchkin, evt) => {
					const { XHR } = this;
					const { order } = evt;
					const { id: orderId } = order;
					const url = `/api/page/requirements/tracking/munchkin/${orderId}`;
					const response = await XHR.ajax({ url });
					const { Email, FirstName, Hash, LastName } = response;
					const mOptions = { Email, FirstName, LastName };

					return Munchkin.munchkinFunction(`associateLead`, mOptions, Hash);
				},
			});

			// Partnerize integration
			this.register({
				provider: `PARTNERIZE`,
				usageType: USAGE_TYPES.marketing,
				obtain: ({ id, params }) => {
					/* Handled entirely in onPurchase */
				},
				onPurchase: async (api, evt) => {
					const { App, XHR } = this;
					const clickref = App.getSavedQueryParam(`clickref`);

					if (this.isString(clickref, 1)) {
						const { cart, id: providerId, location, order, platform, productList, user } = evt;
						const { address = {} } = location;
						const { countryCodeAlpha2: country } = address;
						const { id: conversionref } = order;
						const { currency } = productList;
						const { items, promoCode: voucher } = cart;
						const { id: custref } = user;
						const orderParams = { clickref, conversionref, country, currency, custref, platform, voucher };
						let src = `https://prf.hn/conversion/campaign:${providerId}`;

						this.each(orderParams, (value, key) => {
							if (this.isString(value, 1)) {
								const encodedValue = encodeURIComponent(value);
								src += `/${key}:${encodedValue}`;
							}
						});

						this.each(items, (item) => {
							const { category, id: itemId, price, quantity } = item;
							src += `/[category:${category}/sku:${itemId}/value:${price}/quantity:${quantity}]`;
						});

						await XHR.loadImage({ src });
					}
				},
			});

			// Pinterest integration
			this.register({
				provider: `PINTEREST`,
				usageType: USAGE_TYPES.marketing,
				obtain: async ({ id, params }) => {
					// adapted from api embed snippet

					if (window.pintrk === undefined) {
						const pintrk = function () {
							pintrk.queue.push(Array.prototype.slice.call(arguments));
						};

						pintrk.queue = [];
						pintrk.version = `3.0`;

						window.pintrk = pintrk;
					}

					const deps = [`//s.pinimg.com/ct/core.js`];
					await this.require({ deps });
					const { pintrk: api } = window; // grab the latest reference in case it's changed

					api(`load`, id);
					return api;
				},
				onPageView: (pintrk, evt) => {
					const { platform } = evt;
					return pintrk(`page`, { platform });
				},
				onPurchase: (pintrk, evt) => {
					const { cart, order, platform, productList } = evt;
					const { quantity: order_quantity, total: value } = cart;
					const { currency } = productList;
					const { id: order_id } = order;

					return pintrk(`track`, `checkout`, { currency, order_id, order_quantity, platform, value });
				},
			});

			// Riskified integration
			this.register({
				provider: `RISKIFIED`,
				usageType: USAGE_TYPES.marketing,
				obtain: async ({ id, params, sessionId }) => {
					const domain = this.isString(params.domain, 1) ? params.domain : `www.tixr.com`;
					const deps = [`//beacon.riskified.com?shop=${domain}&sid=${sessionId}`];
					await this.require({ deps });
				},
				onPageView: (api, evt) => {
					/* api was loaded with provided id & params, done */
				},
			});

			const makeSegmentEventType = (options) => {
				const { caseOverride, name, prefix } = options;
				const result = this.isString(prefix, 1) ? `${prefix} ${name}` : name;

				if (caseOverride === `upper`) {
					return result.toUpperCase();
				} else if (caseOverride === `lower`) {
					return result.toLowerCase();
				} else {
					return result;
				}
			};

			const makeSegmentDetails = ({ brand, custom, eventType, location, params, performers, productList, user }) => {
				const { email: emailParam } = params;
				const details = {};

				this.assign(details, custom);

				if ((emailParam === `raw`) & this.isObject(user)) {
					details.email = user.email;
				}

				if (eventType === `page-view`) {
					// doesn't include group / event specific details
					return details;
				}

				// all other segment events can assume having a brand, productList, etc.

				const { endDate, keywords, startDate } = productList;

				this.assign(details, {
					brand: brand.name,
					currency: productList.currency,
					end_date: Time.getDatetime({ timestamp: endDate }),
					start_date: Time.getDatetime({ timestamp: startDate }),
				});

				if (this.isObject(location)) {
					details.address = location.addressString;
				}

				const performer = [];
				const headliner = [];
				this.each(performers, (performer) => {
					const { imageUrl, isHeadliner, name } = performer;
					const entry = { name };

					if (this.isString(imageUrl, 1)) {
						entry.image_url = imageUrl;
					}

					performer.push(entry);

					if (isHeadliner) {
						headliner.push(entry);
					}
				});

				if (headliner.length > 0) {
					details.headliner = headliner;
				}

				if (performer.length > 0) {
					details.performer = performer;
				}

				if (this.isString(keywords, 1)) {
					details.keywords = keywords;
				}

				return details;
			};

			// Segment integration
			this.register({
				provider: `SEGMENT`,
				usageType: USAGE_TYPES.analytics,
				obtain: async ({ id, params }) => {
					// adapted from api embed snippet
					const SNIPPET_VERSION = `4.13.2`;
					const path = `https://cdn.segment.com/analytics.js/v1/${id}/analytics.min.js`;
					const methods = [
						`trackSubmit`,
						`trackClick`,
						`trackLink`,
						`trackForm`,
						`pageview`,
						`identify`,
						`reset`,
						`group`,
						`track`,
						`ready`,
						`alias`,
						`debug`,
						`page`,
						`once`,
						`off`,
						`on`,
						`addSourceMiddleware`,
						`addIntegrationMiddleware`,
						`setAnonymousId`,
						`addDestinationMiddleware`,
					];
					const _loadOptions = {};
					const invoked = true;
					const factory = (method) => {
						return function () {
							const args = Array.prototype.slice.call(arguments);
							args.unshift(method);
							analytics.push(args);
							return analytics;
						};
					};
					const analytics = [];

					this.assign(analytics, { SNIPPET_VERSION, _loadOptions, _writeKey: id, factory, invoked, methods });
					this.each(methods, (key) => (analytics[key] = factory(key)));

					window.analytics = analytics;
					await this.require({ deps: [path] }); // load their script

					return new Promise((resolve) => {
						// explicitly reference the window.analytics reference, in case it changes
						window.analytics.ready(() => resolve(window.analytics));
					});
				},
				onCartAdd: async (analytics, evt) => {
					const { brand, custom, item, location, pageUrl, params, performers, platform, productList, user } = evt;
					const { id: product_id, name, price, quantity, saleEnd, saleStart, thumbUrl } = item;
					const eventType = makeSegmentEventType({
						caseOverride: params.case,
						name: `Product Added`,
						prefix: params.prefix,
					});
					const details = makeSegmentDetails({ brand, custom, location, params, performers, productList, user });
					const options = {};

					this.assign(details, {
						name,
						platform,
						price,
						product_id,
						quantity,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
					});

					if (this.isString(thumbUrl, 1)) {
						details.image_url = thumbUrl;
					}

					if (this.isObject(item.custom)) {
						this.assign(details, item.custom);
					}

					await new Promise((resolve) => analytics.track(eventType, details, options, resolve));
				},
				onCartRemove: async (analytics, evt) => {
					const { brand, custom, item, location, pageUrl, params, performers, platform, productList, user } = evt;
					const { id: product_id, name, price, quantity, saleEnd, saleStart, thumbUrl } = item;
					const eventType = makeSegmentEventType({
						caseOverride: params.case,
						name: `Product Removed`,
						prefix: params.prefix,
					});
					const details = makeSegmentDetails({ brand, custom, location, params, performers, productList, user });
					const options = {};

					this.assign(details, {
						name,
						platform,
						price,
						product_id,
						quantity,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
					});

					if (this.isString(thumbUrl, 1)) {
						details.image_url = thumbUrl;
					}

					if (this.isObject(item.custom)) {
						this.assign(details, item.custom);
					}

					await new Promise((resolve) => analytics.track(eventType, details, options, resolve));
				},
				onCartView: async (analytics, evt) => {
					const { brand, cart, custom, location, params, performers, platform, productList, user } = evt;
					const eventType = makeSegmentEventType({
						caseOverride: params.case,
						name: `Cart Viewed`,
						prefix: params.prefix,
					});
					const details = makeSegmentDetails({ brand, custom, location, params, performers, productList, user });
					const { items } = cart;
					const options = {};

					details.platform = platform;
					details.products = this.map(items, (item) => {
						const result = {
							name: item.name,
							price: item.price,
							product_id: item.id,
							quantity: item.quantity,
							sale_end_date: Time.getDatetime({ timestamp: item.endDate }),
							sale_start_date: Time.getDatetime({ timestamp: item.startDate }),
							sku: item.id,
							url: item.url,
						};

						if (this.isString(item.thumbUrl, 1)) {
							result.image_url = item.thumbUrl;
						}

						if (this.isObject(item.custom)) {
							this.assign(result, item.custom);
						}

						return result;
					});

					await new Promise((resolve) => analytics.track(eventType, details, options, resolve));
				},
				onCheckoutInit: async (analytics, evt) => {
					const { brand, cart, custom, location, pageUrl, params, performers, platform, productList, user } = evt;
					const eventType = makeSegmentEventType({
						caseOverride: params.case,
						name: `Checkout Started`,
						prefix: params.prefix,
					});
					const details = makeSegmentDetails({ brand, custom, location, params, performers, productList, user });
					const options = {};

					this.assign(details, {
						platform,
						products: this.map(cart.items, (item) => {
							const { id: product_id, name, price, quantity, saleEnd, saleStart, thumbUrl } = item;
							const result = {
								name,
								price,
								product_id,
								quantity,
								sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
								sale_start_date: Time.getDatetime({ timestamp: saleStart }),
								sku: product_id,
								url: pageUrl,
							};

							if (this.isString(thumbUrl, 1)) {
								result.image_url = thumbUrl;
							}

							if (this.isObject(item.custom)) {
								this.assign(result, item.custom);
							}

							return result;
						}),
						shipping: cart.shipping,
						value: cart.total,
					});

					await new Promise((resolve) => analytics.track(eventType, details, options, resolve));
				},
				onCheckoutStep: async (analytics, evt) => {
					const { brand, cart, custom, location, params, performers, platform, productList, step, user } = evt;
					const eventType = makeSegmentEventType({
						caseOverride: params.case,
						name: `Checkout Step Viewed`,
						prefix: params.prefix,
					});
					const { paymentMethod, shippingMethod, token: checkout_id } = cart;
					const details = makeSegmentDetails({ brand, custom, location, params, performers, productList, user });
					const options = {};

					this.assign(details, { checkout_id, platform, step });

					if (this.isString(shippingMethod, 1)) {
						details.shipping_method = shippingMethod;
					}

					if (this.isString(paymentMethod, 1)) {
						details.payment_method = paymentMethod;
					}

					await new Promise((resolve) => analytics.track(eventType, details, options, resolve));
				},
				onCheckoutStepComplete: async (analytics, evt) => {
					const { brand, cart, custom, location, params, performers, platform, productList, step, user } = evt;
					const details = makeSegmentDetails({ brand, custom, location, params, performers, productList, user });
					const { paymentMethod, shippingMethod, token: checkout_id } = cart;
					let eventType = makeSegmentEventType({
						caseOverride: params.case,
						name: `Checkout Step Completed`,
						prefix: params.prefix,
					});
					const isPaymentStep = this.includes(PAYMENT_CHECKOUT_STEPS, step);
					const options = {};

					this.assign(details, { checkout_id, platform, step });

					if (this.isString(shippingMethod, 1)) {
						details.shipping_method = shippingMethod;
					}

					if (this.isString(paymentMethod, 1)) {
						details.payment_method = paymentMethod;
					}

					await new Promise((resolve) => analytics.track(eventType, details, options, resolve));

					if (isPaymentStep) {
						eventType = makeSegmentEventType({
							caseOverride: params.case,
							name: `Payment Info Entered`,
							prefix: params.prefix,
						});

						await new Promise((resolve) => analytics.track(eventType, details, options, resolve));
					}
				},
				onPageView: async (analytics, evt) => {
					const { custom, pageType, pageUrl: url, params, path, platform, title, user } = evt;
					const options = {};
					const name = title;
					const details = makeSegmentDetails({ custom, eventType: `page-view`, params, user });

					this.assign(details, { path, platform, title, url });

					await new Promise((resolve) => analytics.page(pageType, name, details, options, resolve));
				},
				onProductClick: async (analytics, evt) => {
					const { brand, custom, location, pageUrl, params, performers, platform, product, productList, user } = evt;
					const eventType = makeSegmentEventType({
						caseOverride: params.case,
						name: `Product Clicked`,
						prefix: params.prefix,
					});
					const details = makeSegmentDetails({ brand, custom, location, params, performers, productList, user });
					const { category, id: product_id, name, price, saleEnd, saleStart, quantityMultiple, thumbUrl } = product;
					const options = {};

					this.assign(details, {
						category,
						name,
						platform,
						price,
						product_id,
						quantity: quantityMultiple,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
						value: price,
					});

					if (this.isString(thumbUrl, 1)) {
						details.image_url = thumbUrl;
					}

					await new Promise((resolve) => analytics.track(eventType, details, options, resolve));
				},
				onProductView: async (analytics, evt) => {
					const { brand, custom, location, pageUrl, params, performers, platform, product, productList, user } = evt;
					const eventType = makeSegmentEventType({
						caseOverride: params.case,
						name: `Product Viewed`,
						prefix: params.prefix,
					});
					const details = makeSegmentDetails({ brand, custom, location, params, performers, productList, user });
					const { category, id: product_id, quantityMultiple, name, price, saleEnd, saleStart, thumbUrl } = product;
					const options = {};

					this.assign(details, {
						category,
						name,
						platform,
						price,
						product_id,
						quantity: quantityMultiple,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
						value: price,
					});

					if (this.isString(thumbUrl, 1)) {
						details.image_url = thumbUrl;
					}

					await new Promise((resolve) => analytics.track(eventType, details, options, resolve));
				},
				onPurchase: async (analytics, evt) => {
					const { brand, cart, custom, location, order, pageUrl, params, performers, platform, productList, user } =
						evt;
					const eventType = makeSegmentEventType({
						caseOverride: params.case,
						name: `Order Completed`,
						prefix: params.prefix,
					});
					const { items, promoCode, shipping, token: checkout_id, total } = cart;
					const { id: order_id } = order;
					const details = makeSegmentDetails({ brand, custom, location, params, performers, productList, user });
					const options = {};

					this.assign(details, {
						checkout_id,
						order_id,
						platform,
						revenue: total,
						shipping,
						total,
						value: total,
					});

					details.products = this.map(items, (item) => {
						const { id: product_id, name, price, quantity, saleEnd, saleStart, thumbUrl } = item;
						const productDetails = {
							name,
							price,
							product_id,
							quantity,
							sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
							sale_start_date: Time.getDatetime({ timestamp: saleStart }),
							sku: product_id,
							url: pageUrl,
						};

						if (this.isString(thumbUrl, 1)) {
							productDetails.image_url = thumbUrl;
						}

						if (this.isObject(item.custom)) {
							this.assign(productDetails, item.custom);
						}

						return productDetails;
					});

					if (this.isString(promoCode, 1)) {
						details.coupon = promoCode;
					}

					// shipping: 3,
					// tax: 2,
					// discount: 2.5,

					await new Promise((resolve) => analytics.track(eventType, details, options, resolve));
				},
				onUserLogIn: async (analytics, evt) => {
					const { user } = evt;
					const { email = ``, firstName = ``, trackingId: userId = ``, lastName = `` } = user || {};
					const traits = { email, firstName, lastName };
					const options = {};

					await new Promise((resolve) => analytics.identify(userId, traits, options, resolve));
				},
				onUserSignUp: async (analytics, evt) => {
					const { user } = evt;
					const { email = ``, firstName = ``, trackingId: userId = ``, lastName = `` } = user || {};
					const traits = { email, firstName, lastName };
					const options = {};

					await new Promise((resolve) => analytics.identify(userId, traits, options, resolve));
				},
				onWishlistAdd: async (analytics, evt) => {
					const { brand, custom, location, pageUrl, params, performers, platform, productList, user } = evt;
					const eventType = makeSegmentEventType({
						caseOverride: params.case,
						name: `Product Added to Wishlist`,
						prefix: params.prefix,
					});
					const details = makeSegmentDetails({ brand, custom, location, params, performers, productList, user });
					const { category, currency, id: product_id, name, saleEnd, saleStart } = productList;
					const options = {};

					this.assign(details, {
						category,
						currency,
						name,
						platform,
						product_id,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
					});

					await new Promise((resolve) => analytics.track(eventType, details, options, resolve));
				},
				onWishlistRemove: async (analytics, evt) => {
					const { brand, custom, location, pageUrl, params, performers, platform, productList, user } = evt;
					const eventType = makeSegmentEventType({
						caseOverride: params.case,
						name: `Product Removed from Wishlist`,
						prefix: params.prefix,
					});
					const details = makeSegmentDetails({ brand, custom, location, params, performers, productList, user });
					const { category, currency, id: product_id, name, saleEnd, saleStart } = productList;
					const options = {};

					this.assign(details, {
						category,
						currency,
						name,
						platform,
						product_id,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
					});

					await new Promise((resolve) => analytics.track(eventType, details, options, resolve));
				},
			});

			// Sift Science integration
			this.register({
				provider: `SIFT_SCIENCE`,
				usageType: USAGE_TYPES.marketing,
				obtain: async ({ id, params, sessionId }) => {
					const deps = [`//cdn.siftscience.com/s.js`, `User`];
					await this.require({ deps });

					const { User } = this;
					const { _sift } = window;

					const userId = await User.get({
						fail: (reason) => ``, // not signed in
						force: false, // allow cached user info
						then: ({ id: userId }) => `${userId}`, // user signed in
					});

					_sift.push([`_setAccount`, id]);
					_sift.push([`_setUserId`, userId]);
					_sift.push([`_setSessionId`, sessionId]);

					return _sift; // return the API, which will be cached
				},

				onPageView: (_sift, evt) => {
					return _sift.push([`_trackPageview`]);
				},
			});

			// Snapchat Pixel integration
			this.register({
				provider: `SNAPCHAT`,
				usageType: USAGE_TYPES.marketing,
				obtain: async ({ id, params }) => {
					if (window.snaptr === undefined) {
						const tr = function () {
							if (tr.handleRequest) {
								tr.handleRequest.apply(tr, arguments);
							} else {
								tr.queue.push(arguments);
							}
						};

						window.snaptr = tr;
						window.snaptr.queue = [];
					}

					const deps = [`https://sc-static.net/scevent.min.js`];
					await this.require({ deps });

					const { snaptr } = window;

					snaptr(`init`, id);
					return snaptr;
				},
				onPageView: (snaptr, evt) => {
					const { platform, uid, user } = evt;

					snaptr(`track`, `PAGE_VIEW`, {
						client_dedup_id: uid,
						platform,
						user_email: this.isObject(user) ? user.email : ``,
						user_id: this.isObject(user) ? user.userId : ``,
						user_phone_number: this.isObject(user) ? user.phoneNumber : ``,
						first_name: this.isObject(user) ? user.firstName : ``,
						last_name: this.isObject(user) ? user.lastName : ``,
					});
				},
				onPurchase: (snaptr, evt) => {
					const { cart, order, platform, productList, uid, user } = evt;
					const { currency } = productList;
					const { quantity: number_items, total: price } = cart;
					const { id: transaction_id } = order;
					const success = 1;

					return snaptr(`track`, `PURCHASE`, {
						currency,
						number_items,
						platform,
						price,
						success,
						transaction_id,
						client_dedup_id: uid,
						user_email: this.isObject(user) ? user.email : ``,
						user_id: this.isObject(user) ? user.userId : ``,
						user_phone_number: this.isObject(user) ? user.phoneNumber : ``,
						first_name: this.isObject(user) ? user.firstName : ``,
						last_name: this.isObject(user) ? user.lastName : ``,
					});
				},
			});

			// TikTok integration
			this.register({
				provider: `TIKTOK`,
				usageType: USAGE_TYPES.marketing,
				obtain: async ({ id, params }) => {
					const deps = [`https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${id}&lib=ttq`];
					let ttq = [];

					ttq.methods = [
						`page`,
						`track`,
						`identify`,
						`instances`,
						`debug`,
						`on`,
						`off`,
						`once`,
						`ready`,
						`alias`,
						`group`,
						`enableCookie`,
						`disableCookie`,
					];
					ttq.setAndDefer = function (t, e) {
						t[e] = function () {
							const argumentsArray = Array.prototype.slice.call(arguments, 0);
							const copy = [e].concat(argumentsArray);
							t.push(copy);
						};
					};

					for (var i = 0; i < ttq.methods.length; i++) {
						ttq.setAndDefer(ttq, ttq.methods[i]);
					}

					ttq.instance = function (t) {
						for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) {
							ttq.setAndDefer(e, ttq.methods[n]);
						}

						return e;
					};

					ttq._i = ttq._i || {};
					ttq._i[id] = [];
					ttq._i[id]._u = i;
					ttq._t = ttq._t || {};
					ttq._t[id] = +new Date();
					ttq._o = ttq._o || {};
					ttq._o[id] = {};

					window.ttq = ttq;
					window.TiktokAnalyticsObject = `ttq`;

					await this.require({ deps });

					ttq = window.ttq; // refresh in case it's changed
					return ttq;
				},
				onCartAdd: (ttq, evt) => {
					const { item, platform, productList, sessionId, uid, user } = evt;
					const { email = ``, id: userId = ``, phoneNumber = `` } = user || {};
					const { id: productId, name, price, quantity } = item;

					ttq.identify({
						email,
						external_id: sessionId || userId,
						phone_number: phoneNumber,
					});

					return ttq.track(
						`AddToCart`,
						{
							content_id: productId,
							content_name: name,
							content_type: `product`,
							currency: productList.currency,
							platform,
							price,
							quantity,
							value: quantity * price,
						},
						{
							event_id: uid,
						}
					);
				},
				onCheckoutInit: (ttq, evt) => {
					const { cart, platform, productList, sessionId, uid, user } = evt;
					const { email = ``, id: userId = ``, phoneNumber = `` } = user || {};
					const { currency } = productList;
					const { items, total: value } = cart;
					const contents = this.map(items, (item) => {
						const { id: content_id, name: content_name, price, quantity } = item;
						return { content_id, content_name, price, quantity };
					});

					ttq.identify({
						email,
						external_id: sessionId || userId,
						phone_number: phoneNumber,
					});

					return ttq.track(`InitiateCheckout`, { contents, currency, platform, value }, { event_id: uid });
				},
				onCheckoutStepComplete: (ttq, evt) => {
					const { cart, platform, productList, step, sessionId, uid, user } = evt;
					const { email = ``, id: userId = ``, phoneNumber = `` } = user || {};
					const isPaymentStep = this.includes(PAYMENT_CHECKOUT_STEPS, step);
					const isNewPaymentStep = this.includes(NEW_PAYMENT_STEPS, step);

					ttq.identify({
						email,
						external_id: sessionId || userId,
						phone_number: phoneNumber,
					});

					if (isNewPaymentStep) {
						ttq.track(`AddPaymentInfo`, { platform }, { event_id: uid });
					}

					if (isPaymentStep) {
						const { total: value } = cart;
						const { currency } = productList;

						ttq.track(`CompletePayment`, { currency, platform, value }, { event_id: uid });
					}
				},
				onPageView: (ttq, evt) => {
					const { platform, sessionId, title, uid } = evt;

					ttq.identify({
						external_id: sessionId || ``,
					});

					return ttq.track(`Pageview`, { content_name: title, platform }, { event_id: uid });
				},
				onPurchase: (ttq, evt) => {
					const { cart, platform, productList, sessionId, uid, user } = evt;
					const { email = ``, id: userId = ``, phoneNumber = `` } = user || {};
					const { items, total: value } = cart;
					const { currency } = productList;
					const contents = [];
					const content_type = `product`;

					this.each(items, (item) => {
						const { id: content_id, name: content_name, price, quantity } = item;

						contents.push({ content_id, content_name, price, quantity });
					});

					ttq.identify({
						email,
						external_id: sessionId || userId,
						phone_number: phoneNumber,
					});

					return ttq.track(`PlaceAnOrder`, { contents, content_type, currency, platform, value }, { event_id: uid });
				},
				onWishlistAdd: (ttq, evt) => {
					const { platform, productList, sessionId, user, uid } = evt;
					const { email = ``, id: userId = ``, phoneNumber = `` } = user || {};
					const { id: content_id } = productList;

					ttq.identify({
						email,
						external_id: sessionId || userId,
						phone_number: phoneNumber,
					});

					return ttq.track(`AddToWishlist`, { content_id, platform }, { event_id: uid });
				},
				onUserLogIn: (ttq, evt) => {
					const { sessionId, user, uid } = evt;
					const { email = ``, id: userId = ``, phoneNumber = `` } = user || {};

					return ttq.identify(
						{
							email,
							external_id: sessionId || userId,
							phone_number: phoneNumber,
						},
						{ event_id: uid }
					);
				},
				onUserSignUp: (ttq, evt) => {
					const { sessionId, user, uid } = evt;
					const { email = ``, id: userId = ``, phoneNumber = `` } = user || {};

					return ttq.identify(
						{
							email,
							external_id: sessionId || userId,
							phone_number: phoneNumber,
						},
						{ event_id: uid }
					);
				},
			});

			// Twitter integration
			this.register({
				provider: `TWITTER`,
				usageType: USAGE_TYPES.marketing,
				obtain: async ({ id, params }) => {
					// adapted from Twitter JS snippet:
					let twq = function () {
						twq.exe ? twq.exe.apply(twq, arguments) : twq.queue.push(arguments);
					};
					twq.version = `1.1`;
					twq.queue = [];
					window.twq = twq;

					const deps = [`//static.ads-twitter.com/uwt.js`];
					await this.require({ deps });

					twq = window.twq;
					twq(`init`, id);
					return twq;
				},
				onPageView: (twq, evt) => {
					const { platform } = evt;
					return twq(`track`, `PageView`, { platform });
				},
				onPurchase: (twq, evt) => {
					const { cart, platform, productList } = evt;
					const { quantity: num_items, total: value } = cart;
					const { currency } = productList;

					return twq(`track`, `Purchase`, { currency, num_items, platform, value });
				},
			});

			// Vertical Mass integration
			this.register({
				provider: `VERTICAL_MASS`,
				usageType: USAGE_TYPES.marketing,
				obtain: async ({ id, params }) => {
					const { placement } = params;

					// adapted from Vertical Mass JS snippet:
					(function (w, d, c, t, p, a) {
						w[c] =
							w[c] ||
							function () {
								(w[c].q = w[c].q || [])[p](arguments);
							};
						// var n = d.createElement(t), s = d.getElementsByTagName(t)[0];
						// n.async = true; n.src = '//lib.vmweb.net/identity.min.js';
						// s.parentNode.insertBefore(n, s);
					})(window, document, `IDENTITY`, `script`, `push`);

					const deps = [`//lib.vmweb.net/identity.min.js`];
					await this.require({ deps });

					const { IDENTITY } = window;

					IDENTITY(`init`, id, placement); // initialize the library
					delete window.IDENTITY; // clear the global scope
					return IDENTITY; // resolve with the api
				},
				onCartAdd: (IDENTITY, evt) => {
					const { item, platform } = evt;
					const { name: label, id } = item;

					return IDENTITY(`track`, `Added Ticket`, { id, label, platform });
				},
				onProductListView: (IDENTITY, evt) => {
					const { platform, productList } = evt;
					const { name: productListName, products } = productList;

					this.each(products, (product) => {
						const { name: productName, id } = product;
						const label = `${productListName}-${productName}`;

						IDENTITY(`track`, `Viewed Ticket`, { id, label, platform });
					});
				},
				onPageView: (IDENTITY, evt) => IDENTITY(`trackPageview`),
				onPurchase: (IDENTITY, evt) => {
					const { cart, order, platform, productList } = evt;
					const { items } = cart;
					const { name: productListName } = productList;
					const { id: orderId } = order;

					this.each(items, (item) => {
						const { id: productId, name, quantity } = item;

						if (quantity > 0) {
							IDENTITY(`track`, `Viewed Ticket`, {
								id: productId,
								label: `${productListName}-${name}`,
								// revenue: sale.price * 100, //please pass total cents, e.g. $21.99 is 2199
								quantity,
								//please include any other important metadata about the event
							});
						}
					});

					return IDENTITY(`track`, `Completed Order`, {
						id: orderId,
						platform,
						// revenue: total * 100 // please pass total cents, e.g. $21.99 is 2199
					});
				},
			});

			// AnyTrack
			const makeAnyTrackProductList = ({ items }) => {
				return this.map(items, (item) => {
					const { id, name, price, quantity } = item;
					const result = { id, name, price, quantity };

					return result;
				});
			};

			this.register({
				provider: `ANYTRACK`,
				usageType: USAGE_TYPES.analytics,
				obtain: async ({ id, params }) => {
					if (!this.isString(id, 1)) {
						throw new Error(`AnyTrack requires a Property ID`);
					}

					// // adapted from AnyTrack JS snippet:
					// // ---------------------------------
					// ! function(e, t, n, s, a) {
					// 	(a = t.createElement(n)).async = !0,
					// 	a.src = `https://assets.anytrack.io/${id}.js`,
					// 	(t = t.getElementsByTagName(n)[0]).parentNode.insertBefore(a, t),
					// 	e[s] = e[s] || function() {
					// 		(e[s].q = e[s].q || []).push(arguments)
					// 	}
					// }(window, document, "script", "AnyTrack");

					if (!window.AnyTrack) {
						let _AnyTrack = function () {
							(_AnyTrack.q = _AnyTrack.q || []).push(arguments);
						};
						window.AnyTrack = _AnyTrack;
					}

					const deps = [`//assets.anytrack.io/${id}.js`];

					await this.require({ deps });

					return window.AnyTrack;
				},
				onCartAdd: (AnyTrack, evt) => {
					const { brand, item, platform, productList, user } = evt;
					const { currency } = productList;
					const { value } = item;
					const email = this.isObject(user) ? user.email : null;
					const group_id = this.isObject(brand) ? brand.id : null;
					const items = makeAnyTrackProductList({ items: [item] });
					const trackingParams = { brand: brand.name, currency, email, items, platform, tixr_client: group_id, value };

					return window.AnyTrack(`trigger`, `AddToCart`, trackingParams);
				},
				onCartView: (AnyTrack, evt) => {
					const { brand, cart, platform, productList, user } = evt;
					const { currency } = productList;
					const { total: value } = cart;
					const email = this.isObject(user) ? user.email : null;
					const group_id = this.isObject(brand) ? brand.id : null;
					const items = makeAnyTrackProductList({ items: cart.items });
					const trackingParams = { brand: brand.name, currency, email, items, platform, tixr_client: group_id, value };

					return window.AnyTrack(`trigger`, `ViewContent`, trackingParams);
				},
				onCheckoutInit: (AnyTrack, evt) => {
					const { brand, cart, platform, productList, user } = evt;
					const { currency } = productList;
					const { total: value } = cart;
					const email = this.isObject(user) ? user.email : null;
					const group_id = this.isObject(brand) ? brand.id : null;
					const items = makeAnyTrackProductList({ items: cart.items });
					const trackingParams = { currency, email, items, platform, tixr_client: group_id, value };

					return window.AnyTrack(`trigger`, `InitiateCheckout`, trackingParams);
				},
				onCheckoutStepComplete: (AnyTrack, evt) => {
					const { brand, cart, platform, productList, user } = evt;
					const { currency } = productList;
					const { total: value } = cart;
					const email = this.isObject(user) ? user.email : null;
					const group_id = this.isObject(brand) ? brand.id : null;
					const items = makeAnyTrackProductList({ items: cart.items });
					const trackingParams = { currency, email, items, platform, tixr_client: group_id, value };

					return window.AnyTrack(`trigger`, `AddPaymentInfo`, trackingParams);
				},
				onPageView: (AnyTrack, evt) => {
					const { brand, cart, pageUrl: page_location, platform, productList, title: page_title, user } = evt;
					const brand_name = this.isObject(brand) ? brand.name : null;
					const currency = this.isObject(productList) ? productList.currency : null;
					const value = this.isObject(cart) ? cart.total : null;
					const items = this.isObject(cart) ? makeAnyTrackProductList({ items: cart.items }) : [];
					const group_id = this.isObject(brand) ? brand.id : null;
					const email = this.isObject(user) ? user.email : null;
					const trackingParams = {
						brand: brand_name,
						currency,
						email,
						items,
						platform,
						url: page_location,
						label: page_title,
						tixr_client: group_id,
						value,
					};

					return window.AnyTrack(`trigger`, `ViewContent`, trackingParams);
				},
				onProductListView: (AnyTrack, evt) => {
					const { brand, platform, productList, user } = evt;
					const { currency, products } = productList;
					const group_id = this.isObject(brand) ? brand.id : null;
					const items = this.map(products, (product) => ({
						id: product.id,
						name: product.name,
						price: product.price,
						quantity: 0,
					}));
					const email = this.isObject(user) ? user.email : null;
					const trackingParams = { brand: brand.name, currency, email, items, platform, tixr_client: group_id };

					return window.AnyTrack(`trigger`, `ViewContent`, trackingParams);
				},
				onProductView: (AnyTrack, evt) => {
					const { brand, platform, product, productList, user } = evt;
					const { currency } = productList;
					const email = this.isObject(user) ? user.email : null;
					const group_id = this.isObject(brand) ? brand.id : null;
					const items = [{ id: product.id, name: product.name, price: product.price, quantity: 1 }];
					const trackingParams = {
						brand: brand.name,
						currency,
						email,
						items,
						platform,
						value: product.price,
						tixr_client: group_id,
					};

					return window.AnyTrack(`trigger`, `ViewContent`, trackingParams);
				},
				onPurchase: (AnyTrack, evt) => {
					const { cart, order, platform, productList, user } = evt;
					const { quantity, total: value } = cart;
					const { id: transactionId } = order;
					const { currency } = productList;
					const items = makeAnyTrackProductList({ items: cart.items });
					const email = this.isObject(user) ? user.email : null;
					const trackingParams = { currency, email, items, platform, quantity, transactionId, value }; // see https://readme.anytrack.io/docs/event-attributes for event attributes

					return window.AnyTrack(`trigger`, `Purchase`, trackingParams);
				},
				onUserLogIn: (AnyTrack, evt) => {
					const { platform, user } = evt;
					const email = this.isObject(user) ? user.email : null;
					const trackingParams = { email, platform };

					return window.AnyTrack(`trigger`, `CompleteRegistration`, trackingParams);
				},
				onUserSignUp: (AnyTrack, evt) => {
					const { platform, user } = evt;
					const email = this.isObject(user) ? user.email : null;
					const trackingParams = { email, platform };

					return window.AnyTrack(`trigger`, `CompleteRegistration`, trackingParams);
				},
			});

			// Hightouch integration

			const makeHightouchEventType = (options) => {
				const { caseOverride, name, prefix } = options;
				const result = this.isString(prefix, 1) ? `${prefix} ${name}` : name;

				if (caseOverride === `upper`) {
					return result.toUpperCase();
				} else if (caseOverride === `lower`) {
					return result.toLowerCase();
				} else {
					return result;
				}
			};

			const makeHightouchDetails = ({
				brand,
				custom,
				externalId,
				location,
				performers,
				productList,
				productType,
				uid,
				user,
				_fbc,
				_fbp,
			}) => {
				const details = {};

				this.assign(details, {
					_fbc,
					_fbp,
					custom,
					external_id: externalId,
					product_type: productType,
					uuid: uid,
				});

				if (this.isObject(user)) {
					this.assign(details, {
						birthday: user.birthday,
						email: user.email,
						first_name: user.firstName,
						gender: user.gender,
						last_name: user.lastName,
						phone_number: user.phoneNumber,
						postal_code: user.postalCode,
					});
				}

				if (this.isObject(productList)) {
					this.assign(details, {
						currency: productList.currency,
						end_date: Time.getDatetime({ timestamp: productList.endDate }),
						start_date: Time.getDatetime({ timestamp: productList.startDate }),
					});

					if (this.isString(productList.keywords, 1)) {
						this.assign(details, { keywords: productList.keywords });
					}
				}

				if (this.isObject(brand)) {
					this.assign(details, { brand: brand.name });
				}

				if (this.isObject(location)) {
					this.assign(details, {
						event_address: location.addressString,
						event_city: location.city,
						event_postal_code: location.postalCode,
						event_state: location.state,
						event_country: location.countryCodeAlpha2,
					});
				}

				const performer = [];
				const headliner = [];

				if (this.isArray(performers)) {
					this.each(performers, (performer) => {
						const { imageUrl, isHeadliner, name } = performer;
						const entry = { name };

						if (this.isString(imageUrl, 1)) {
							entry.image_url = imageUrl;
						}

						performer.push(entry);

						if (isHeadliner) {
							headliner.push(entry);
						}
					});
				}

				if (headliner.length > 0) {
					this.assign(details, { headliner });
				}

				if (performer.length > 0) {
					this.assign(details, { performer });
				}

				return details;
			};

			this.register({
				provider: `HIGHTOUCH`,
				usageType: USAGE_TYPES.analytics,
				obtain: async ({ id, params }) => {
					const SNIPPET_VERSION = `0.0.1`;
					const apiHost = `us-east-1.hightouch-events.com`;
					const path = `https://cdn.hightouch-events.com/browser/release/v1-latest/events.min.js`;
					const methods = [
						`trackSubmit`,
						`trackClick`,
						`trackLink`,
						`trackForm`,
						`pageview`,
						`identify`,
						`reset`,
						`group`,
						`track`,
						`ready`,
						`alias`,
						`debug`,
						`page`,
						`once`,
						`off`,
						`on`,
						`addSourceMiddleware`,
						`addIntegrationMiddleware`,
						`setAnonymousId`,
						`addDestinationMiddleware`,
					];
					const _loadOptions = { apiHost };
					const invoked = true;
					const factory = (method) => {
						return function () {
							const args = Array.prototype.slice.call(arguments);
							args.unshift(method);
							htevents.push(args);
							return htevents;
						};
					};
					const htevents = [];

					this.assign(htevents, { SNIPPET_VERSION, _loadOptions, _writeKey: id, factory, invoked, methods });
					this.each(methods, (key) => (htevents[key] = factory(key)));

					window.htevents = htevents;
					await this.require({ deps: [path] }); // load their script

					return new Promise((resolve) => {
						// explicitly reference the window.htevents reference, in case it changes
						window.htevents.ready(() => resolve(window.htevents));
					});
				},
				onCartAdd: async (htevents, evt) => {
					const { App } = this;
					const {
						brand,
						custom,
						item,
						location,
						pageUrl,
						params,
						performers,
						platform,
						productList,
						sessionId,
						uid,
						user,
					} = evt;
					const { category, id: product_id, name, price, quantity, saleEnd, saleStart, thumbUrl } = item;
					const eventType = makeHightouchEventType({
						caseOverride: params.case,
						name: `AddToCart`,
						prefix: params.prefix,
					});
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						brand,
						custom,
						externalId: sessionId,
						location,
						performers,
						productList,
						productType: category,
						uid,
						user,
						_fbc,
						_fbp,
					});
					const options = {};

					this.assign(details, {
						name,
						platform,
						price,
						product_id,
						quantity,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
					});

					if (this.isString(thumbUrl, 1)) {
						details.image_url = thumbUrl;
					}

					if (this.isObject(item.custom)) {
						this.assign(details, item.custom);
					}

					await new Promise((resolve) => htevents.track(eventType, details, options, resolve));
				},
				onCartRemove: async (htevents, evt) => {
					const { App } = this;
					const {
						brand,
						custom,
						item,
						location,
						pageUrl,
						params,
						performers,
						platform,
						productList,
						sessionId,
						uid,
						user,
					} = evt;
					const { category, id: product_id, name, price, quantity, saleEnd, saleStart, thumbUrl } = item;
					const eventType = makeHightouchEventType({
						caseOverride: params.case,
						name: `RemoveFromCart`,
						prefix: params.prefix,
					});
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						brand,
						custom,
						externalId: sessionId,
						location,
						performers,
						productList,
						productType: category,
						uid,
						user,
						_fbc,
						_fbp,
					});
					const options = {};

					this.assign(details, {
						name,
						platform,
						price,
						product_id,
						quantity,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
					});

					if (this.isString(thumbUrl, 1)) {
						details.image_url = thumbUrl;
					}

					if (this.isObject(item.custom)) {
						this.assign(details, item.custom);
					}

					await new Promise((resolve) => htevents.track(eventType, details, options, resolve));
				},
				onCartView: async (htevents, evt) => {
					const { App } = this;
					const { brand, cart, custom, location, params, performers, platform, productList, sessionId, uid, user } =
						evt;
					const eventType = makeHightouchEventType({
						caseOverride: params.case,
						name: `ViewContent`,
						prefix: params.prefix,
					});
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						brand,
						custom,
						externalId: sessionId,
						location,
						performers,
						productList,
						uid,
						user,
						_fbc,
						_fbp,
					});
					const { items } = cart;
					const options = {};

					details.platform = platform;
					details.products = this.map(items, (item) => {
						const result = {
							name: item.name,
							price: item.price,
							product_id: item.id,
							product_type: item.category,
							quantity: item.quantity,
							sale_end_date: Time.getDatetime({ timestamp: item.endDate }),
							sale_start_date: Time.getDatetime({ timestamp: item.startDate }),
							sku: item.id,
							url: item.url,
						};

						if (this.isString(item.thumbUrl, 1)) {
							result.image_url = item.thumbUrl;
						}

						if (this.isObject(item.custom)) {
							this.assign(result, item.custom);
						}

						return result;
					});

					await new Promise((resolve) => htevents.track(eventType, details, options, resolve));
				},
				onCheckoutInit: async (htevents, evt) => {
					const { App } = this;
					const {
						brand,
						cart,
						custom,
						location,
						pageUrl,
						params,
						performers,
						platform,
						productList,
						sessionId,
						uid,
						user,
					} = evt;
					const eventType = makeHightouchEventType({
						caseOverride: params.case,
						name: `InitiateCheckout`,
						prefix: params.prefix,
					});
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						brand,
						custom,
						externalId: sessionId,
						location,
						performers,
						productList,
						uid,
						user,
						_fbc,
						_fbp,
					});
					const options = {};

					this.assign(details, {
						platform,
						products: this.map(cart.items, (item) => {
							const { category, id: product_id, name, price, quantity, saleEnd, saleStart, thumbUrl } = item;
							const result = {
								name,
								price,
								product_id,
								product_type: category,
								quantity,
								sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
								sale_start_date: Time.getDatetime({ timestamp: saleStart }),
								sku: product_id,
								url: pageUrl,
							};

							if (this.isString(thumbUrl, 1)) {
								result.image_url = thumbUrl;
							}

							if (this.isObject(item.custom)) {
								this.assign(result, item.custom);
							}

							return result;
						}),
						shipping: cart.shipping,
						value: cart.total,
					});

					await new Promise((resolve) => htevents.track(eventType, details, options, resolve));
				},
				onCheckoutStep: async (htevents, evt) => {
					const { App } = this;
					const {
						brand,
						cart,
						custom,
						location,
						params,
						performers,
						platform,
						productList,
						sessionId,
						step,
						uid,
						user,
					} = evt;
					const eventType = makeHightouchEventType({
						caseOverride: params.case,
						name: `CheckoutStepViewed`,
						prefix: params.prefix,
					});
					const { paymentMethod, shippingMethod, token: checkout_id } = cart;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						brand,
						custom,
						externalId: sessionId,
						location,
						performers,
						productList,
						uid,
						user,
						_fbc,
						_fbp,
					});
					const options = {};

					this.assign(details, { checkout_id, platform, step });

					if (this.isString(shippingMethod, 1)) {
						details.shipping_method = shippingMethod;
					}

					if (this.isString(paymentMethod, 1)) {
						details.payment_method = paymentMethod;
					}

					await new Promise((resolve) => htevents.track(eventType, details, options, resolve));
				},
				onCheckoutStepComplete: async (htevents, evt) => {
					const { App } = this;
					const {
						brand,
						cart,
						custom,
						location,
						params,
						performers,
						platform,
						productList,
						sessionId,
						step,
						uid,
						user,
					} = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						brand,
						custom,
						externalId: sessionId,
						location,
						performers,
						productList,
						uid,
						user,
						_fbc,
						_fbp,
					});
					const { paymentMethod, shippingMethod, token: checkout_id } = cart;
					const isNewPaymentStep = this.includes(NEW_PAYMENT_STEPS, step);
					const isPaymentStep = this.includes(PAYMENT_CHECKOUT_STEPS, step);
					let eventName = `CheckoutStepCompleted`;

					if (isPaymentStep) {
						eventName = `PaymentInfoEntered`;
					} else if (isNewPaymentStep) {
						eventName = `AddPaymentInfo`;
					}

					const eventType = makeHightouchEventType({
						caseOverride: params.case,
						name: eventName,
						prefix: params.prefix,
					});

					const options = {};

					this.assign(details, { checkout_id, platform, step });

					if (this.isString(shippingMethod, 1)) {
						details.shipping_method = shippingMethod;
					}

					if (this.isString(paymentMethod, 1)) {
						details.payment_method = paymentMethod;
					}

					await new Promise((resolve) => htevents.track(eventType, details, options, resolve));

					if (isPaymentStep) {
						await new Promise((resolve) => htevents.track(eventType, details, options, resolve));
					}
				},
				onPageView: async (htevents, evt) => {
					const { App } = this;
					const { custom, pageType, pageUrl: url, path, platform, sessionId, title, uid, user } = evt;
					const options = {};
					const name = title;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						custom,
						externalId: sessionId,
						uid,
						user,
						_fbc,
						_fbp,
					});

					this.assign(details, { path, platform, title, url });

					await new Promise((resolve) => htevents.page(pageType, name, details, options, resolve));
				},
				onProductClick: async (htevents, evt) => {
					const { App } = this;
					const {
						brand,
						custom,
						location,
						pageUrl,
						params,
						performers,
						platform,
						product,
						productList,
						sessionId,
						uid,
						user,
					} = evt;
					const eventType = makeHightouchEventType({
						caseOverride: params.case,
						name: `ProductClicked`,
						prefix: params.prefix,
					});
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						brand,
						custom,
						externalId: sessionId,
						location,
						performers,
						productList,
						uid,
						user,
						_fbc,
						_fbp,
					});
					const { category, id: product_id, name, price, saleEnd, saleStart, quantityMultiple, thumbUrl } = product;
					const options = {};

					this.assign(details, {
						category,
						name,
						platform,
						price,
						product_id,
						quantity: quantityMultiple,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
						value: price,
					});

					if (this.isString(thumbUrl, 1)) {
						details.image_url = thumbUrl;
					}

					await new Promise((resolve) => htevents.track(eventType, details, options, resolve));
				},
				onProductView: async (htevents, evt) => {
					const { App } = this;
					const {
						brand,
						custom,
						location,
						pageUrl,
						params,
						performers,
						platform,
						product,
						productList,
						sessionId,
						uid,
						user,
					} = evt;
					const eventType = makeHightouchEventType({
						caseOverride: params.case,
						name: `ViewContent`,
						prefix: params.prefix,
					});
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						brand,
						custom,
						externalId: sessionId,
						location,
						params,
						performers,
						productList,
						uid,
						user,
						_fbc,
						_fbp,
					});
					const { category, id: product_id, quantityMultiple, name, price, saleEnd, saleStart, thumbUrl } = product;
					const options = {};

					this.assign(details, {
						category,
						name,
						platform,
						price,
						product_id,
						quantity: quantityMultiple,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
						value: price,
					});

					if (this.isString(thumbUrl, 1)) {
						details.image_url = thumbUrl;
					}

					await new Promise((resolve) => htevents.track(eventType, details, options, resolve));
				},
				onPurchase: async (htevents, evt) => {
					const { App } = this;
					const {
						brand,
						cart,
						custom,
						location,
						order,
						pageUrl,
						params,
						performers,
						platform,
						productList,
						sessionId,
						uid,
						user,
					} = evt;
					const eventType = makeHightouchEventType({
						caseOverride: params.case,
						name: `Purchase`,
						prefix: params.prefix,
					});
					const { items, promoCode, shipping, token: checkout_id, total } = cart;
					const { id: order_id } = order;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						brand,
						custom,
						externalId: sessionId,
						location,
						params,
						performers,
						productList,
						uid,
						user,
						_fbc,
						_fbp,
					});
					const options = {};

					this.assign(details, {
						checkout_id,
						order_id,
						platform,
						revenue: total,
						shipping,
						total,
						value: total,
					});

					details.products = this.map(items, (item) => {
						const { category, id: product_id, name, price, quantity, saleEnd, saleStart, thumbUrl } = item;
						const productDetails = {
							name,
							price,
							product_id,
							product_type: category,
							quantity,
							sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
							sale_start_date: Time.getDatetime({ timestamp: saleStart }),
							sku: product_id,
							url: pageUrl,
						};

						if (this.isString(thumbUrl, 1)) {
							productDetails.image_url = thumbUrl;
						}

						if (this.isObject(item.custom)) {
							this.assign(productDetails, item.custom);
						}

						return productDetails;
					});

					if (this.isString(promoCode, 1)) {
						details.coupon = promoCode;
					}

					await new Promise((resolve) => htevents.track(eventType, details, options, resolve));
				},
				onUserLogIn: async (htevents, evt) => {
					const { App } = this;
					const { sessionId, uid, user } = evt;
					const { email = ``, firstName = ``, id: userId = ``, lastName = ``, phoneNumber = `` } = user || {};
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const traits = {
						_fbc,
						_fbp,
						email,
						external_id: sessionId,
						firstName,
						lastName,
						phoneNumber,
						uuid: uid,
					};
					const options = {};

					await new Promise((resolve) => htevents.identify(userId, traits, options, resolve));
				},
				onUserSignUp: async (htevents, evt) => {
					const { App } = this;
					const { sessionId, uid, user } = evt;
					const { email = ``, firstName = ``, id: userId = ``, lastName = ``, phoneNumber = `` } = user || {};
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const traits = {
						_fbc,
						_fbp,
						email,
						external_id: sessionId,
						firstName,
						lastName,
						phoneNumber,
						uuid: uid,
					};
					const options = {};

					await new Promise((resolve) => htevents.identify(userId, traits, options, resolve));
				},
				onWishlistAdd: async (htevents, evt) => {
					const { App } = this;
					const { brand, custom, location, pageUrl, params, performers, platform, productList, sessionId, uid, user } =
						evt;
					const eventType = makeHightouchEventType({
						caseOverride: params.case,
						name: `ProductAddedToWishlist`,
						prefix: params.prefix,
					});
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						brand,
						custom,
						externalId: sessionId,
						location,
						params,
						performers,
						productList,
						uid,
						user,
						_fbc,
						_fbp,
					});
					const { category, currency, id: product_id, name, saleEnd, saleStart } = productList;
					const options = {};

					this.assign(details, {
						category,
						currency,
						name,
						platform,
						product_id,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
					});

					await new Promise((resolve) => htevents.track(eventType, details, options, resolve));
				},
				onWishlistRemove: async (htevents, evt) => {
					const { App } = this;
					const { brand, custom, location, pageUrl, params, performers, platform, productList, sessionId, uid, user } =
						evt;
					const eventType = makeHightouchEventType({
						caseOverride: params.case,
						name: `ProductRemovedFromWishlist`,
						prefix: params.prefix,
					});
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const details = makeHightouchDetails({
						brand,
						custom,
						externalId: sessionId,
						location,
						params,
						performers,
						productList,
						uid,
						user,
						_fbc,
						_fbp,
					});
					const { category, currency, id: product_id, name, saleEnd, saleStart } = productList;
					const options = {};

					this.assign(details, {
						category,
						currency,
						name,
						platform,
						product_id,
						sale_end_date: Time.getDatetime({ timestamp: saleEnd }),
						sale_start_date: Time.getDatetime({ timestamp: saleStart }),
						sku: product_id,
						url: pageUrl,
					});

					await new Promise((resolve) => htevents.track(eventType, details, options, resolve));
				},
			});

			// Customers.ai

			const setCustomersAIAttributes = ({ xrayData, user, externalId, _fbc, _fbp }) => {
				if (this.isObject(user)) {
					xrayData(`set_attribute`, `birthday`, user.birthday);
					xrayData(`set_attribute`, `email`, user.email);
					xrayData(`set_attribute`, `first_name`, user.firstName);
					xrayData(`set_attribute`, `gender`, user.gender);
					xrayData(`set_attribute`, `last_name`, user.lastName);
					xrayData(`set_attribute`, `phone_number`, user.phoneNumber);
					xrayData(`set_attribute`, `postal_code`, user.postalCode);
				}

				xrayData(`set_attribute`, `_fbc`, _fbc);
				xrayData(`set_attribute`, `_fbp`, _fbp);
				xrayData(`set_attribute`, `external_id`, externalId);
			};

			this.register({
				provider: `CUSTOMERS_AI`,
				usageType: USAGE_TYPES.analytics,
				obtain: async ({ id, params }) => {
					if (!window.xrayData) {
						window.xrayDataLayer = window.xrayDataLayer || [];

						let xrayData = function () {
							window.xrayDataLayer.push(arguments);
						};
						window.xrayData = xrayData;
					}

					const deps = [`//mm-uxrv.com/js/business_${id}.js`];

					await this.require({ deps });

					return window.xrayData;
				},
				onCartAdd: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=add_to_cart&platform=${platform}`);
				},
				onCartRemove: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=remove_from_cart&platform=${platform}`);
				},
				onCartView: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=cart_view&platform=${platform}`);
				},
				onCheckoutInit: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=begin_checkout&platform=${platform}`);
				},
				onCheckoutStep: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=checkout_step_viewed&platform=${platform}`);
				},
				onCheckoutStepComplete: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, step, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();
					const isPaymentStep = this.includes(PAYMENT_CHECKOUT_STEPS, step);
					const eventName = isPaymentStep ? `payment_info_entered` : `checkout_step_completed`;

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=${eventName}&platform=${platform}`);
				},
				onPageView: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=page_view&platform=${platform}`);
				},
				onProductClick: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=product_clicked&platform=${platform}`);
				},
				onProductView: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=product_viewed&platform=${platform}`);
				},
				onPurchase: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=purchase&platform=${platform}`);
				},
				onUserLogIn: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=user_logged_in&platform=${platform}`);
				},
				onUserSignUp: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=user_signed_up&platform=${platform}`);
				},
				onWishlistAdd: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=product_added_to_wishlist&platform=${platform}`);
				},
				onWishlistRemove: (xrayData, evt) => {
					const { App } = this;
					const { pageUrl, platform, sessionId, user } = evt;
					const queryParams = App.getQueryParams();
					const { fbclid } = queryParams;
					const _fbc = getFBC(fbclid);
					const _fbp = getFBP();

					setCustomersAIAttributes({ xrayData, user, externalId: sessionId, _fbc, _fbp });

					return xrayData(`page_view`, `${pageUrl}?cust-ai-evt=product_removed_from_wishlist&platform=${platform}`);
				},
			});
		},

		api: {
			/**
			 * Parameters that is passed into Insights.register() that define how to handle a 3rd party API integration.
			 * @typedef {Object} InsightRegistration
			 * @property {string} provider - The key / name of the provider, e.g. "Faceook" or "Segment"
			 * @property {function} obtain - A handler to be called to obtain an initialized 3rd party API, when no cached API exists.
			 * @property {?function|null} makeCacheKey - What key to cache the API under. If a function is provided, it will be called with the id and parameters the key is needed.
			 * @property {?function} onCartAdd - Specify a handler, to make any 3rd party calls required when a product is added to the cart.
			 * @property {?function} onCartRemove - Specify a handler, to make any 3rd party calls required when a product is removed from cart.
			 * @property {?function} onCartView - Specify a handler, to make any 3rd party calls required when the cart details are viewed.
			 * @property {?function} onCheckoutInit - Specify a handler, to make any 3rd party calls required when checkout begins.
			 * @property {?function} onCheckoutStep - Specify a handler, to make any 3rd party calls required when a checkout step is loaded.
			 * @property {?function} onCheckoutStepComplete - Specify a handler, to make any 3rd party calls required when a checkout step is completed.
			 * @property {?function} onPageView - Specify a handler, to make any 3rd party calls required when a pageview occurs.
			 * @property {?function} onPurchase - Specify a handler, to make any 3rd party calls required when a purchase is made.
			 * @property {?function} onProductClick - Specify a handler, to make any 3rd party calls required when a product is clicked.
			 * @property {?function} onProductListView - Specify a handler, to make any 3rd party calls required when a group of products is viewed.
			 * @property {?function} onProductView - Specify a handler, to make any 3rd party calls required when a product is viewed.
			 * @property {?function} onSocialShare - Specify a handler, to make any 3rd party calls required when an event is shared via social media.
			 * @property {?function} onUpgradeDeselectOriginalTicket - Specify a handler, to make any 3rd party calls required when a user exits the upgrade form.
			 * @property {?function} onUpgradeExitForm - Specify a handler, to make any 3rd party calls required when a user exits the upgrade modal cancelling upgrade flow.
			 * @property {?function} onUpgradeLoadForm - Specify a handler, to make any 3rd party calls required when a user loads the upgrade form (e.g. by clicking "Upgrades Available" badge on event card from My Events).
			 * @property {?function} onUpgradeSeeAvailable - Specify a handler, to make any 3rd party calls required when a user clicks "See Available Upgrades"
			 * @property {?function} onUpgradeSelectOriginalTicket - Specify a handler, to make any 3rd party calls required when a user exits the upgrade form.
			 * @property {?function} onUserLogIn - Specify a handler, to make any 3rd party calls required when a user signs into their account.
			 * @property {?function} onUserSignUp - Specify a handler, to make any 3rd party calls required when a user creates an account.
			 * @property {?function} onWishlistAdd - Specify a handler, to make any 3rd party calls required when a user follows an event.
			 * @property {?function} onWishlistRemove - Specify a handler, to make any 3rd party calls required when a user unfollows an event.
			 */

			/**
			 * Register a 3rd party API integration.
			 * @method Insights#register
			 * @param {InsightRegistration} options - The options for the 3rd party API integration.
			 */
			register: {
				public: true,
				value(context, options) {
					const { providers } = this;
					const {
						obtain,
						onCartAdd,
						onCartRemove,
						onCartView,
						onCheckoutInit,
						onCheckoutStep,
						onCheckoutStepComplete,
						onCommerceInit,
						onEventView,
						onPageInit,
						onPageView,
						onProductClick,
						onProductListView,
						onProductView,
						onPurchase,
						onUpgradeDeselectOriginalTicket,
						onUpgradeExitForm,
						onUpgradeLoadForm,
						onUpgradeSeeAvailable,
						onUpgradeSelectOriginalTicket,
						onSocialShare,
						onUserLogIn,
						onUserSignUp,
						onWishlistAdd,
						onWishlistRemove,
						provider: key,
						usageType,
					} = options;
					const provider = {
						usageType,
						obtain,
						onCartAdd,
						onCartRemove,
						onCartView,
						onCheckoutInit,
						onCheckoutStep,
						onCheckoutStepComplete,
						onCommerceInit,
						onEventView,
						onPageInit,
						onPageView,
						onProductClick,
						onProductListView,
						onProductView,
						onPurchase,
						onSocialShare,
						onUpgradeDeselectOriginalTicket,
						onUpgradeExitForm,
						onUpgradeLoadForm,
						onUpgradeSeeAvailable,
						onUpgradeSelectOriginalTicket,
						onUserLogIn,
						onUserSignUp,
						onWishlistAdd,
						onWishlistRemove,
					};
					let { makeCacheKey } = options;

					if (!this.isString(key, 1)) {
						throw new Error(`Insights.register(options.key) parameter is required.`);
					}

					if (!this.isFunction(obtain)) {
						throw new Error(`Insights.register(options.obtain) parameter is required.`);
					}

					if (makeCacheKey === undefined) {
						// cache based on id, default
						makeCacheKey = ({ id }) => `${key}-${id}`;
					} else if (makeCacheKey === null) {
						// cache single result
						makeCacheKey = () => `${key}-static`;
					} else if (typeof makeCacheKey !== `function`) {
						// make sure a function was passed
						throw new Error(`Insights.register(options.makeCacheKey) parameter must be a function or null.`);
					}

					this.assign(provider, { makeCacheKey });
					providers[key] = provider;
				},
			},

			// Used for true page changes, when the tracking configurations change
			init: {
				public: true,
				async: true,
				value(context, options) {
					const { data } = this;
					const { config } = options;

					data.trackingConfig = config;
					data.path = options.path; // cache the url for quick reference to the current page

					// used for firing any 3rd party logic that needs to run ASAP
					this.capture({ eventType: `commerce-init` });
				},
			},

			/**
			 * @method Insights#getSessionId
			 */
			getSessionId: {
				value() {
					const { App, Utils } = this;
					let sessionId = App.getCookie(CLIENT_SESSION_ID); // check for an existing cookie value

					if (!this.isString(sessionId, 1)) {
						sessionId = Utils.generateGuid(); // generate a new guid to represent this user's session

						App.setCookie(CLIENT_SESSION_ID, sessionId); // set it for future page loads
					}

					return sessionId;
				},
			},

			/**
			 * @method Insights#getUserId
			 * @returns null || string
			 */
			getUserId: {
				value() {
					const { App } = this;
					const firstPartyStorage = App.getFirstPartyStorage();

					if (!this.isObject(firstPartyStorage)) {
						return null;
					}

					const { userId } = firstPartyStorage;

					return userId;
				},
			},

			/**
			 * @method Insights.setUid
			 */
			setUid: {
				public: true,
				value(context, { uid }) {
					const { data } = this;

					if (!this.isString(uid, 1)) {
						const random = Math.random();
						uid = `r.${random}`;
					}

					data.uid = uid;
				},
			},

			/**
			 * @method Insights.capture
			 */
			capture: {
				public: true,
				async value(context, options) {
					await this.require({ deps: [`App`] });
					const { App, Config, System, Time, Utils, data, providers } = this;
					const { PRIMARY_DOMAIN } = Config;
					const {
						allowFunctional,
						allowAnalytics,
						allowMarketing,
						trackingConfig,
						customTrackingConfig,
						trackingQueue,
					} = data;
					const { custom, eventType, path: passedPath, user } = options;
					const trackingKeys = EVENT_TYPE_MAP[eventType];
					const { config: configKey, handler: handlerKey } = trackingKeys;
					const configList = trackingConfig[configKey];
					const customConfigList = customTrackingConfig[configKey];
					const basePath = App.getPath();
					const random = Utils.random(1000000);
					const timestamp = Time.getTime();
					const uid = `${data.uid}-${timestamp}-${random}`;
					const path = this.isString(passedPath) ? passedPath : data.path;
					const sessionId = this.getSessionId();
					const system = System.get();
					const platform = system.platform || `web`;

					let pageUrl = `${PRIMARY_DOMAIN}${path}`;

					if (eventType === `page-view`) {
						if (data.firstPageView) {
							const query = App.getQueryString();
							const hash = App.getHashString();

							// TODO: some level of enforcement, in case these are already in the URL
							if (this.isString(query, 1)) {
								pageUrl += `?${query}`;
							}
							if (this.isString(hash, 1)) {
								pageUrl += `#${hash}`;
							}

							data.firstPageView = false;
						}
					}

					/**
					 * @typedef {object} InsightObject
					 * @property {object} [custom] - adds the ability to pass custom attributes
					 */

					/**
					 * @typedef {InsightObject} InsightBrand
					 * @property {string} id
					 * @property {string} name
					 */

					/**
					 * @typedef {InsightObject} InsightCart
					 * @property {InsightCartItem[]} items
					 * @property {?string} promoCode
					 * @property {number} quantity
					 * @property {?string} paymentMethod
					 * @property {number} shipping
					 * @property {?string} shippingMethod
					 * @property {number} subtotal
					 * @property {number} total
					 */

					/**
					 * @typedef {InsightProduct} InsightCartItem
					 * @property {number} quantity
					 * @property {number} value
					 */

					/**
					 * @typedef {InsightObject} InsightOrder
					 * @property {string} id
					 */

					/**
					 * @typedef {InsightObject} InsightPerformer
					 * @property {?string} imageUrl
					 * @property {boolean} isHeadliner
					 * @property {string} name
					 */

					/**
					 * @typedef {InsightObject} InsightProduct
					 * @property {string} category
					 * @property {?number} holdId
					 * @property {string} id
					 * @property {string} keywords
					 * @property {string} name
					 * @property {number} price
					 * @property {number} saleEnd
					 * @property {number} saleStart
					 * @property {?string} thumbUrl
					 */

					/**
					 * @typedef {InsightObject} InsightProductList
					 * @property {string} category
					 * @property {string} currency
					 * @property {string} id
					 * @property {string} keywords
					 * @property {string} name
					 * @property {number} saleEndDate
					 * @property {number} saleStartDate
					 * @property {number} startDate
					 * @property {number} endDate
					 * @property {Product[]} products
					 */

					/**
					 * @typedef {InsightObject} InsightAddress
					 * @property {string} streetAddress
					 * @property {?string} extendedAddress
					 * @property {?string} city
					 * @property {?string} state
					 * @property {?string} postalCode
					 * @property {string} countryCodeAlpha2
					 */

					/**
					 * @typedef {InsightObject} InsightLocation
					 * @property {InsightAddress} address
					 * @property {?string} addressString
					 * @property {?string} name
					 */

					/**
					 * @typedef {InsightObject} InsightUser
					 * @property {string} email
					 * @property {string} emailHash
					 * @property {string} firstName
					 * @property {string} lastName
					 */

					/**
					 * Information that will be passed into every handler function when the tracked event fires.
					 * @typedef {object} InsightEvent
					 * @property {string} basePath
					 * @property {string} id
					 * @property {object} params
					 * @property {string} path
					 * @property {string} random
					 * @property {string} timestamp
					 * @property {string} uid
					 * @property {string} pageUrl
					 * @property {string} sessionId - GUID to represent this user's session
					 * @property {string} platform - does the event originate from mobile web, desktop web, or native tixr/team app?
					 * @property {?string} targetUrl - Used for the "Generic" provider
					 * @property {?InsightUser} user
					 */
					const insightEvent = {
						basePath,
						custom,
						pageUrl,
						path,
						random,
						sessionId,
						platform,
						timestamp,
						uid,
						user,
					};

					switch (
						eventType // attach additional event properties based on eventType
					) {
						/**
						 * @typedef {InsightEvent} CartChangeInsightEvent
						 * @property {InsightBrand} brand
						 * @property {InsightCart} cart
						 * @property {InsightLocation} location
						 * @property {InsightCartItem} item
						 * @property {InsightPerformer[]} performers
						 * @property {InsightProductList} productList
						 * @property {string} purchaseFlow
						 */
						case `cart-add`:
						case `cart-remove`: {
							const {
								brand,
								cart,
								location,
								item,
								orderProtectionFee,
								paymentPlanFee,
								productList,
								purchaseFlow,
								purchaseFlowItem,
							} = options;
							this.assign(insightEvent, {
								brand,
								cart,
								location,
								item,
								orderProtectionFee,
								paymentPlanFee,
								productList,
								purchaseFlow,
								purchaseFlowItem,
							});
							break;
						}

						/**
						 * @typedef {InsightEvent} CartViewInsightEvent
						 * @property {InsightBrand} brand
						 * @property {InsightCart} cart
						 * @property {InsightLocation} location
						 * @property {InsightPerformer[]} performers
						 * @property {InsightProductList} productList
						 * @property {string} purchaseFlow
						 */
						case `cart-view`: {
							const {
								brand,
								cart,
								location,
								orderProtectionFee,
								paymentPlanFee,
								productList,
								purchaseFlow,
								purchaseFlowItem,
							} = options;
							this.assign(insightEvent, {
								brand,
								cart,
								location,
								orderProtectionFee,
								paymentPlanFee,
								productList,
								purchaseFlow,
								purchaseFlowItem,
							});
							break;
						}

						/**
						 * @typedef {InsightEvent} CheckoutInitInsightEvent
						 * @property {InsightBrand} brand
						 * @property {InsightCart} cart
						 * @property {InsightLocation} location
						 * @property {InsightPerformer[]} performers
						 * @property {InsightProductList} productList
						 * @property {string} purchaseFlow
						 */
						case `checkout-init`: {
							const {
								brand,
								cart,
								location,
								orderProtectionFee,
								paymentPlanFee,
								productList,
								purchaseFlow,
								purchaseFlowItem,
							} = options;
							this.assign(insightEvent, {
								brand,
								cart,
								location,
								orderProtectionFee,
								paymentPlanFee,
								productList,
								purchaseFlow,
								purchaseFlowItem,
							});
							break;
						}

						/**
						 * @typedef {InsightEvent} CheckoutStepInsightEvent
						 * @property {InsightBrand} brand
						 * @property {InsightCart} cart
						 * @property {InsightLocation} location
						 * @property {InsightPerformer[]} performers
						 * @property {InsightProductList} productList
						 * @property {string} purchaseFlow
						 * @property {string} step
						 * @property {number} stepNumber
						 */
						case `checkout-step`:
						case `checkout-step-complete`: {
							const {
								brand,
								cart,
								location,
								orderProtectionFee,
								paymentPlanFee,
								productList,
								purchaseFlow,
								purchaseFlowItem,
								step,
								user,
							} = options;
							const stepIndex = CHECKOUT_STEPS.indexOf(step);
							const stepNumber = stepIndex + 1;
							this.assign(insightEvent, {
								brand,
								cart,
								location,
								orderProtectionFee,
								paymentPlanFee,
								productList,
								purchaseFlow,
								purchaseFlowItem,
								step,
								stepNumber,
								user,
							});
							break;
						}

						/**
						 * @typedef {InsightEvent} PageViewInsightEvent
						 * @property {?InsightBrand} brand
						 * @property {string} pageType
						 * @property {?InsightProductList} productList
						 * @property {string} title
						 * @property {string} title
						 * @property {string} purchaseFlow
						 */
						case `page-view`: {
							// Log a page view
							this.defaults(options, {
								pageType: `Other`,
								title: document.title,
							});
							const {
								brand = null,
								cart = null,
								orderProtectionFee,
								pageType,
								productList = null,
								purchaseFlow,
								purchaseFlowItem,
								title,
							} = options;

							this.assign(insightEvent, {
								brand,
								cart,
								orderProtectionFee,
								pageType,
								productList,
								purchaseFlow,
								purchaseFlowItem,
								title,
							});
							break;
						}

						/**
						 * @typedef {InsightEvent} ProductListViewInsightEvent
						 * @property {InsightBrand} brand
						 * @property {InsightLocation} location
						 * @property {InsightPerformer[]} performers
						 * @property {InsightProductList} productList
						 */
						case `product-list-view`: {
							const { brand, location, productList } = options;
							this.assign(insightEvent, { brand, location, productList });
							break;
						}

						/**
						 * @typedef {InsightEvent} ProductInsightEvent
						 * @property {InsightBrand} brand
						 * @property {InsightLocation} location
						 * @property {InsightPerformer[]} performers
						 * @property {Product} product
						 */
						case `product-click`:
						case `product-view`: {
							const { brand, location, product, productList } = options;
							this.assign(insightEvent, { brand, location, product, productList });
							break;
						}

						/**
						 * @typedef {InsightEvent} PurchaseInsightEvent
						 * @property {InsightBrand} brand
						 * @property {InsightCart} cart
						 * @property {InsightLocation} location
						 * @property {InsightOrder} order
						 * @property {InsightPerformer[]} performers
						 * @property {InsightProductList} productList
						 * @property {string} purchaseFlow
						 */
						case `purchase`: {
							const {
								brand,
								cart,
								location,
								order,
								orderProtectionFee,
								paymentPlanFee,
								productList,
								purchaseFlow,
								purchaseFlowItem,
							} = options;
							this.assign(insightEvent, {
								brand,
								cart,
								location,
								order,
								orderProtectionFee,
								paymentPlanFee,
								productList,
								purchaseFlow,
								purchaseFlowItem,
							});
							break;
						}

						/**
						 * @typedef {InsightEvent} SocialShareInsightEvent
						 * @property {InsightBrand} brand
						 * @property {?InsightLocation} location
						 * @property {string} method - e.g. facebook, twitter
						 * @property {?InsightPerformer[]} performers
						 * @property {?ProductList} productList
						 * @property {string} type - product or productList
						 */
						case `social-share`: {
							// the user has used a social network to share something
							const { brand, location, performers, method, productList, type } = options;
							this.assign(insightEvent, { brand, location, performers, method, productList, type });
							break;
						}

						/**
						 * @typedef {InsightEvent} WishlistInsightEvent
						 * @property {ProductList} productList
						 * @property {string} type
						 */
						case `wishlist-add`:
						case `wishlist-remove`: {
							// the user followed or unfollowed something
							const { brand, location, performers, productList, type } = options;
							this.assign(insightEvent, { brand, location, performers, productList, type });
							break;
						}

						/**
						 * @typedef {InsightEvent} UserAuthenticateInsightEvent
						 * @property {string} method
						 */
						case `user-log-in`:
						case `user-sign-up`: {
							const { method } = options;
							this.assign(insightEvent, { method });

							// TODO: start passing method in options

							break;
						}

						/**
						 * @typedef {InsightEvent} UpgradeFormInsightEvent
						 * @property {string} purchaseFlow
						 */
						case `upgrade-exit-form`:
						case `upgrade-see-available`:
						case `upgrade-load-form`: {
							const { purchaseFlow } = options;
							this.assign(insightEvent, { purchase_flow_item: purchaseFlow });
							break;
						}

						/**
						 * @typedef {InsightEvent} UpgradeDeselectOriginalTicketInsightEvent
						 * @property {string} brand,
						 * @property {string} category,
						 * @property {string} id,
						 * @property {string} name,
						 * @property {string} productType,
						 * @property {string} purchaseFlow
						 * @property {number} quantity,
						 *
						 * @typedef {InsightEvent} UpgradeSelectOriginalTicketInsightEvent
						 * @property {string} brand,
						 * @property {string} category,
						 * @property {string} id,
						 * @property {string} name,
						 * @property {string} productType,
						 * @property {string} purchaseFlow
						 * @property {number} quantity,
						 */
						case `upgrade-deselect-original-ticket`:
						case `upgrade-select-original-ticket`: {
							const { brand, category, id, name, productType, purchaseFlow, quantity } = options;
							this.assign(insightEvent, {
								item_brand: brand,
								item_category: category,
								item_id: id,
								item_name: name,
								productType,
								purchase_flow_item: purchaseFlow,
								quantity,
							});
							break;
						}
					}

					const configIterator = (config) => {
						const { provider: providerKeyString } = config;
						const mappedProviderKey = LEGACY_MAP[providerKeyString];
						const providerKey = mappedProviderKey === undefined ? providerKeyString : mappedProviderKey;
						const provider = providers[providerKey];
						const handler = provider[handlerKey];
						const { usageType } = provider;

						if (!this.isFunction(handler)) {
							// skip this iteration
							return;
						}

						const trackingOptions = { config, eventType, handlerKey, insightEvent, providerKey, usageType };

						if (
							(usageType === USAGE_TYPES.analytics && !allowAnalytics) ||
							(usageType === USAGE_TYPES.functional && !allowFunctional) ||
							(usageType === USAGE_TYPES.marketing && !allowMarketing)
						) {
							// queue the tracking call for later if user has denied the provider's required cookie type permission
							trackingQueue.push(trackingOptions); // queue up to potentially fire later
						} else {
							// tracking call is allowed, fire immediately
							this.doTracking(trackingOptions);
						}
					};

					// iterate over all the configured providers for this event type
					this.each(configList, configIterator);
					this.each(customConfigList, configIterator);
				},
			},

			/**
			 * @method Insights.setAllowCapture
			 * @param {Object} options
			 * @param {Boolean} options.allowFunctional - Whether or not to allow functional cookie events.
			 * @param {Boolean} options.allowAnalytics - Whether or not to allow analytics cookie events.
			 * @param {Boolean} options.allowMarketing - Whether or not to allow marketing cookie events.
			 * new overload: Insights.setAllowCapture({ allowFunctional: false, allowAnalytics: false, allowMarketing: false });
			 */
			setAllowCapture: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { allowFunctional, allowAnalytics, allowMarketing } = options;

					this.assign(data, { allowFunctional, allowAnalytics, allowMarketing });
					this.checkTrackingQueue();
				},
			},

			checkTrackingQueue: {
				value() {
					const { data } = this;
					const { allowFunctional, allowAnalytics, allowMarketing, trackingQueue } = data;
					const usageTypePermissions = {
						analytics: allowAnalytics,
						functional: allowFunctional,
						marketing: allowMarketing,
					};

					this.extract(trackingQueue, (trackingOptions) => {
						const { usageType } = trackingOptions;
						const approved = usageTypePermissions[usageType];

						if (approved) {
							this.doTracking(trackingOptions); // notify providers of the tracking event
							return true; // remove from the queue
						}
					});
				},
			},

			doTracking: {
				async value(options) {
					// execute a specific tracking interaction
					const { promises, providers } = this;
					const { providerApis, providerActions } = promises;
					const { config, handlerKey, insightEvent, providerKey } = options;
					const { id, url: providerUrl } = config;
					const provider = providers[providerKey];
					const handler = provider[handlerKey];
					const { sessionId } = insightEvent;

					const onError = (error) => {
						this.log({
							// skip this iteration
							type: `warning`,
							message: `Tracking call failed. config: %O, error: %O`,
							args: [config, error],
						});

						providerActions[providerKey] = null;
						// swallow error
					};

					try {
						let providerPromise = providerActions[providerKey];
						let cache = providerApis[providerKey];

						if (!this.isObject(cache)) {
							cache = {};
							providerApis[providerKey] = cache;
						}

						// thread actions from the same provider, so only one thing happens at a time
						await providerPromise;

						providerPromise = new Promise((resolve, reject) => {
							// be sure to thread any tracking calls into Promise chains
							const { params = {} } = config;

							// avoid changing the foundation object
							const insightEventClone = this.clone(insightEvent);
							// add the provider properties for this tracking entry
							const coreProperties = { id, params };

							if (this.isString(providerUrl)) {
								coreProperties.targetUrl = providerUrl;
							}

							// pass through any extra params returned with the entry
							this.assign(insightEventClone, coreProperties);

							// assemble the key the api will be cached under
							const cacheKey = provider.makeCacheKey({ id, params });
							let promise = cache[cacheKey];

							if (promise === undefined) {
								promise = provider.obtain({ id, params, sessionId });
								cache[cacheKey] = promise;
							}

							// emergency five second timeout
							this.defer({ duration: 5000, then: reject });

							// wait for the api to be ready
							promise = this.when({
								promise,
								// call the handler with the relevant details
								then: (api) => handler(api, insightEventClone),
							});

							promise.then(resolve, reject);
						}).catch(onError);

						providerActions[providerKey] = providerPromise;
						return providerPromise;
					} catch (reason) {
						onError(reason);
					}
				},
			},

			// // Permanent/custom tracking entries, e.g. on App.js:
			// const anyTrackConfig = {
			// 	provider: `ANYTRACK`,
			// 	id: `abcd1234`,
			// };
			// const tracking = {
			// 	cart_add: [anyTrackConfig],
			// 	cart_init: [anyTrackConfig],
			// 	cart_checkout: [anyTrackConfig],
			// 	conversion: [anyTrackConfig],
			// 	page: [anyTrackConfig],
			// };

			// this.isFunction(Insights.addCustomTracking) &&
			// 	Insights.addCustomTracking({
			// 		tracking,
			// 	});

			addCustomTracking: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { tracking } = options;
					const { customTrackingConfig } = data;

					this.each(tracking, (providers, eventType) => {
						const customTracking = customTrackingConfig[eventType];
						this.each(providers, (provider) => {
							customTracking.push(provider);
						});
					});
				},
			},
		},
	});
});

//# sourceMappingURL=Insights.js.map